import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Activity, Send, RotateCcw, Settings, Zap, Wifi, Thermometer,
  HardDrive, Trash2, Power, Clock, Terminal, Save, Download,
  Edit3, Check, X, Camera, FlaskConical, AlertTriangle, History, Battery, Cpu, Radio,
  Droplets, Wind, Leaf, Image, Shield, User, UserPlus, UserMinus, Search, ExternalLink,
  RefreshCw, Heart, ChevronRight, CheckCircle
} from 'lucide-react'
import { useAdminData } from '../context/AdminDataContext'
import { useDeviceStatus } from '../context/DeviceStatusContext'
import { adminApi } from '../services/ApiService'
import { WS_MESSAGE_TYPES } from '../constants/config'
import TelemetryChart from '../components/TelemetryChart'
import DeviceLogs from '../components/DeviceLogs'
import DebugConsole from '../components/DebugConsole'
import './DeviceDetail.css'

const TABS = [
  { key: 'status', label: 'Estado' },
  { key: 'telemetry', label: 'Telemetría' },
  { key: 'commands', label: 'Comandos' },
  { key: 'logs', label: 'Logs' },
  { key: 'history', label: 'Historial' },
  { key: 'security', label: 'Seguridad' },
  { key: 'errors', label: 'Errores' },
]

const HARDWARE_PROFILES = [
  { value: 'Uruguay-v1.0', label: 'Uruguay' },
  { value: 'Pipi-v1.0', label: 'Pipi' },
]

export default function DeviceDetail() {
  const { mac } = useParams()
  const navigate = useNavigate()
  const { devices } = useAdminData()
  const { onDeviceEvent } = useDeviceStatus()

  const decodedMac = decodeURIComponent(mac || '')
  const contextDevice = useMemo(() => devices.find(d => (d.MAC || d.mac) === decodedMac), [devices, decodedMac])

  // For unassigned devices not in context, load directly from API
  const [apiDevice, setApiDevice] = useState(null)
  const [apiLoading, setApiLoading] = useState(false)

  useEffect(() => {
    if (!decodedMac) return
    setApiLoading(true)
    adminApi.get(`/admin/devices/${encodeURIComponent(decodedMac)}`).then(data => {
      if (data && !data.error) setApiDevice(data)
    }).catch(() => { }).finally(() => setApiLoading(false))
  }, [decodedMac])

  // Merge: contextDevice has room/user/firmware data, apiDevice has fresh liveData.heartbeat
  const device = useMemo(() => {
    if (!contextDevice && !apiDevice) return null
    if (!contextDevice) return apiDevice
    if (!apiDevice) return contextDevice
    return { ...contextDevice, liveData: apiDevice.liveData || contextDevice.liveData, isOnline: apiDevice.isOnline ?? contextDevice.isOnline }
  }, [contextDevice, apiDevice])

  const [tab, setTab] = useState('status')
  const [deviceStatus, setDeviceStatus] = useState(null)
  const [statusLoading, setStatusLoading] = useState(false)

  // Command log
  const [cmdLog, setCmdLog] = useState([])
  const cmdLogRef = useRef(null)
  const [cmdLoading, setCmdLoading] = useState(false)

  // Telemetry
  const [telemetryHours, setTelemetryHours] = useState(6)

  // Config pusher

  const [configMode, setConfigMode] = useState('')
  const [configLoading, setConfigLoading] = useState(false)

  // Hardware profile
  const [profileTarget, setProfileTarget] = useState('')
  const [profileLoading, setProfileLoading] = useState(false)
  const [detectedProfile, setDetectedProfile] = useState(null)

  // Reset config
  const [resetLoading, setResetLoading] = useState(false)

  // Switch toggle
  const [switchTogglingStates, setSwitchTogglingStates] = useState({})

  // Rename switch
  const [renamingSwitch, setRenamingSwitch] = useState(null)
  const [renameValue, setRenameValue] = useState('')
  const [renameLoading, setRenameLoading] = useState(false)

  // NVS backup
  const [nvsLoading, setNvsLoading] = useState(false)

  // OTA update
  const [otaLoading, setOtaLoading] = useState(false)

  // Raw MQTT
  const [rawPayload, setRawPayload] = useState('')
  const [rawLoading, setRawLoading] = useState(false)

  // Assign device modal
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [assignLoading, setAssignLoading] = useState(false)
  const [assignClients, setAssignClients] = useState([])
  const [assignClientsLoading, setAssignClientsLoading] = useState(false)
  const [assignSearch, setAssignSearch] = useState('')
  const [assignSelectedUser, setAssignSelectedUser] = useState(null)
  const [assignRooms, setAssignRooms] = useState([])
  const [assignRoomsLoading, setAssignRoomsLoading] = useState(false)
  const [assignSelectedRoom, setAssignSelectedRoom] = useState(null)

  // Unassign device modal
  const [showUnassignModal, setShowUnassignModal] = useState(false)
  const [unassignLoading, setUnassignLoading] = useState(false)

  // pH Calibration (GP-HIDRO)
  const [phTrimV, setPhTrimV] = useState(null)
  const [phCalV4, setPhCalV4] = useState('')
  const [phCalV7, setPhCalV7] = useState('')
  const [phCalV10, setPhCalV10] = useState('')
  const [phRefNeutral, setPhRefNeutral] = useState(6.86)  // buffer neutro: 6.86 o 7.00
  const [phReadLoading, setPhReadLoading] = useState(false)
  const [phCalLoading, setPhCalLoading] = useState(false)
  const [soilCalChannel, setSoilCalChannel] = useState(0)
  const [soilCalDryRaw, setSoilCalDryRaw] = useState('')
  const [soilCalWetRaw, setSoilCalWetRaw] = useState('')
  const [soilCalLoading, setSoilCalLoading] = useState(false)
  const [soilCalAck, setSoilCalAck] = useState(null)

  // Device errors tab
  const [deviceErrors, setDeviceErrors] = useState([])
  const [errorsLoading, setErrorsLoading] = useState(false)
  const [errorsSeverity, setErrorsSeverity] = useState('')

  const currentProfile = detectedProfile || device?.liveData?.hardwareName || null

  // ── Load device status on mount ──
  const loadDeviceStatus = useCallback(async () => {
    if (!decodedMac) return
    setStatusLoading(true)
    try {
      const data = await adminApi.get(`/admin/devices/${encodeURIComponent(decodedMac)}`)
      if (data.status) {
        const statusObj = typeof data.status === 'string' ? JSON.parse(data.status) : data.status
        if (statusObj.data) setDeviceStatus(statusObj.data)
        else if (statusObj.device || statusObj.switches) setDeviceStatus(statusObj)
      }
    } catch (e) {
      console.error('Error fetching device status:', e)
    }
    // Also request fresh status via MQTT
    try {
      await adminApi.post('/admin/commands/status', { mac: decodedMac })
    } catch { }
    setStatusLoading(false)
  }, [decodedMac])

  useEffect(() => { loadDeviceStatus() }, [loadDeviceStatus])

  // ── Load device errors ──
  const loadDeviceErrors = useCallback(async (severity = '') => {
    if (!decodedMac) return
    setErrorsLoading(true)
    try {
      const params = { limit: 100 }
      if (severity) params.severity = severity
      const data = await adminApi.get(`/admin/monitoring/device-errors/${encodeURIComponent(decodedMac)}`, params)
      if (data?.errors) setDeviceErrors(data.errors)
    } catch (e) {
      console.warn('Error fetching device errors:', e)
    }
    setErrorsLoading(false)
  }, [decodedMac])

  useEffect(() => {
    if (tab === 'errors') loadDeviceErrors(errorsSeverity)
  }, [tab, loadDeviceErrors, errorsSeverity])

  // ── Scroll log to bottom ──
  useEffect(() => {
    if (cmdLogRef.current) cmdLogRef.current.scrollTop = cmdLogRef.current.scrollHeight
  }, [cmdLog])

  // ── WebSocket subscriptions ──
  useEffect(() => {
    if (!onDeviceEvent || !decodedMac) return

    let _logSeq = 0
    const appendLog = (type, data) => {
      const id = `${Date.now()}_${++_logSeq}`
      setCmdLog(prev => [...prev.slice(-49), {
        id,
        ts: new Date().toLocaleTimeString('es-AR'),
        type,
        data,
      }])
    }

    const unsubStatus = onDeviceEvent(WS_MESSAGE_TYPES.STATUS_RESPONSE, ({ mac: evMac, data }) => {
      if (evMac === decodedMac) {
        appendLog('STATUS_RESPONSE', data)
        if (data) {
          setDeviceStatus(data)
          const hw = data?.device?.hardware || data?.data?.device?.hardware
          if (hw) setDetectedProfile(hw)
        }
      }
    })
    const unsubRestart = onDeviceEvent(WS_MESSAGE_TYPES.RESTART_RESPONSE, ({ mac: evMac, data }) => {
      if (evMac === decodedMac) appendLog('RESTART_RESPONSE', data)
    })
    const unsubOTA = onDeviceEvent(WS_MESSAGE_TYPES.OTA_RESPONSE, ({ mac: evMac, data }) => {
      if (evMac === decodedMac) appendLog('OTA_RESPONSE', data)
    })
    const unsubConfig = onDeviceEvent(WS_MESSAGE_TYPES.CONFIG_RESPONSE, ({ mac: evMac, data }) => {
      if (evMac === decodedMac) {
        // data = device's inner response fields (thingName/receivedAt already stripped)
        if (data?.type === 'ph_trimv_response' && data?.trimV != null) {
          appendLog('← TrimV recibido', `${Number(data.trimV).toFixed(4)} V`)
          setPhTrimV(data.trimV)
        } else if (data?.type === 'config_response' && data?.channel != null) {
          // GP-SOIL SET_CONFIG ACK
          appendLog('← Calibración ACK', `Ch${data.channel}: dryRaw=${data.dryRaw} wetRaw=${data.wetRaw} cambiado=${data.changed}`)
          setSoilCalAck(data)
        } else {
          appendLog('CONFIG_RESPONSE', data)
        }
      }
    })

    return () => { unsubStatus(); unsubRestart(); unsubOTA(); unsubConfig() }
  }, [onDeviceEvent, decodedMac])

  // ── Helpers ──
  const formatUptime = (seconds) => {
    if (!seconds && seconds !== 0) return '—'
    const d = Math.floor(seconds / 86400)
    const h = Math.floor((seconds % 86400) / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    if (d > 0) return `${d}d ${h}h`
    if (h > 0) return `${h}h ${m}m`
    return `${m}m`
  }

  const getSignalInfo = (rssi) => {
    if (rssi >= -50) return { label: 'Excelente', color: '#10b981' }
    if (rssi >= -60) return { label: 'Buena', color: '#22c55e' }
    if (rssi >= -70) return { label: 'Regular', color: '#f59e0b' }
    return { label: 'Débil', color: '#ef4444' }
  }

  const getResetReasonLabel = (code) => {
    const reasons = {
      1: 'Power On',
      2: 'External Reset',
      3: 'Software Reset',
      4: 'Panic / Exception',
      5: 'Interrupt WDT',
      6: 'Task WDT',
      7: 'Other WDT',
      8: 'Deep Sleep',
      9: 'Brownout',
      10: 'SDIO Reset',
    }
    return reasons[code] || `Desconocido (${code})`
  }

  const getResetReasonColor = (code) => {
    if ([4, 5, 6, 7].includes(code)) return '#ef4444'
    if (code === 9) return '#f59e0b'
    if ([1, 2, 8].includes(code)) return '#10b981'
    return '#94a3b8'
  }

  // ── Actions ──
  const sendCommand = async (action) => {
    if (!decodedMac) return
    setCmdLoading(true)
    setCmdLog(prev => [...prev, { id: Date.now(), ts: new Date().toLocaleTimeString('es-AR'), type: `→ ${action}`, data: {} }])
    try {
      const endpoint = action === 'STATUS' ? '/admin/commands/status' : '/admin/commands/restart'
      if (action === 'RESTART' && !window.confirm(`¿Reiniciar ${device?.name || decodedMac}?`)) { setCmdLoading(false); return }
      const result = await adminApi.post(endpoint, { mac: decodedMac })
      if (!result?.success) throw new Error(result?.error || 'Command failed')
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✓ Enviado', data: result.message || 'OK' }])
    } catch (e) {
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✗ Error', data: e.message, isError: true }])
    } finally { setCmdLoading(false) }
  }

  const sendConfig = async () => {
    if (!decodedMac) return
    const config = {}
    if (configMode !== '') config.mode = configMode
    if (Object.keys(config).length === 0) return
    setConfigLoading(true)
    setCmdLog(prev => [...prev, { id: Date.now(), ts: new Date().toLocaleTimeString('es-AR'), type: '→ SET_CONFIG', data: config }])
    try {
      const result = await adminApi.post('/admin/commands/config', { mac: decodedMac, config })
      if (!result?.success) throw new Error(result?.error || 'Config push failed')
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✓ Config enviado', data: result.message || 'OK' }])
      setConfigMode('')
    } catch (e) {
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✗ Error', data: e.message, isError: true }])
    } finally { setConfigLoading(false) }
  }

  const sendProfileChange = async () => {
    if (!decodedMac || !profileTarget) return
    if (!window.confirm(`¿Cambiar perfil de hardware a "${profileTarget}"?\nEsto envía una actualización OTA. El dispositivo se reiniciará.`)) return
    setProfileLoading(true)
    setCmdLog(prev => [...prev, { id: Date.now(), ts: new Date().toLocaleTimeString('es-AR'), type: '→ PROFILE_CHANGE', data: { targetProfile: profileTarget } }])
    try {
      const result = await adminApi.post('/admin/commands/ota', { mac: decodedMac, hardwareName: profileTarget })
      if (!result?.success) throw new Error(result?.error || 'Profile change failed')
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✓ OTA enviado', data: `Perfil: ${profileTarget} — ${result.message || 'OK'}` }])
      setProfileTarget('')
    } catch (e) {
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✗ Error', data: e.message, isError: true }])
    } finally { setProfileLoading(false) }
  }

  const sendOTAUpdate = async () => {
    if (!decodedMac) return
    if (!window.confirm(`¿Enviar actualización OTA a ${device?.name || decodedMac}?\nEl dispositivo descargará el firmware más reciente y se reiniciará.`)) return
    setOtaLoading(true)
    setCmdLog(prev => [...prev, { id: Date.now(), ts: new Date().toLocaleTimeString('es-AR'), type: '→ OTA_UPDATE', data: { mac: decodedMac } }])
    try {
      const result = await adminApi.post('/admin/commands/ota', { mac: decodedMac })
      if (!result?.success) throw new Error(result?.error || 'OTA update failed')
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✓ OTA enviado', data: result.message || 'OK' }])
    } catch (e) {
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✗ Error', data: e.message, isError: true }])
    } finally { setOtaLoading(false) }
  }

  const sendResetConfig = async () => {
    if (!decodedMac) return
    if (!window.confirm(`¿Restablecer configuración de ${device?.name || decodedMac}?\nTodos los switches volverán a modo básico.`)) return
    setResetLoading(true)
    setCmdLog(prev => [...prev, { id: Date.now(), ts: new Date().toLocaleTimeString('es-AR'), type: '→ RESET_CONFIG', data: { config: 'basico' } }])
    try {
      const result = await adminApi.post('/admin/commands/reset-config', { mac: decodedMac })
      if (!result?.success) throw new Error(result?.error || 'Reset config failed')
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✓ Config reseteada', data: result.message || 'OK' }])
    } catch (e) {
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✗ Error', data: e.message, isError: true }])
    } finally { setResetLoading(false) }
  }

  const toggleSwitch = async (index) => {
    if (!decodedMac) return
    const currentState = deviceStatus?.switches?.[index]?.estado
    setSwitchTogglingStates(prev => ({ ...prev, [index]: true }))
    try {
      const result = await adminApi.post('/admin/commands/switch', { mac: decodedMac, switchIndex: index + 1, state: !currentState })
      if (result.success) {
        setDeviceStatus(prev => {
          if (!prev?.switches) return prev
          const sw = [...prev.switches]
          sw[index] = { ...sw[index], estado: !currentState }
          return { ...prev, switches: sw }
        })
      }
    } catch (e) {
      setCmdLog(prev => [...prev, { id: Date.now(), ts: new Date().toLocaleTimeString('es-AR'), type: '✗ Switch Error', data: e.message, isError: true }])
    } finally {
      setSwitchTogglingStates(prev => ({ ...prev, [index]: false }))
    }
  }

  const startRename = (index) => {
    setRenamingSwitch(index)
    setRenameValue(deviceStatus?.switches?.[index]?.nombre || '')
  }

  const sendRename = async () => {
    if (renamingSwitch === null || !renameValue.trim()) return
    setRenameLoading(true)
    try {
      const result = await adminApi.post('/admin/commands/rename-switch', { mac: decodedMac, switchIndex: renamingSwitch, nombre: renameValue.trim() })
      if (!result?.success) throw new Error(result?.error || 'Rename failed')
      setDeviceStatus(prev => {
        if (!prev?.switches) return prev
        const sw = [...prev.switches]
        sw[renamingSwitch] = { ...sw[renamingSwitch], nombre: renameValue.trim() }
        return { ...prev, switches: sw }
      })
      setCmdLog(prev => [...prev, { id: Date.now(), ts: new Date().toLocaleTimeString('es-AR'), type: '✓ Renombrado', data: `Switch ${renamingSwitch + 1} → ${renameValue.trim()}` }])
      setRenamingSwitch(null)
    } catch (e) {
      setCmdLog(prev => [...prev, { id: Date.now(), ts: new Date().toLocaleTimeString('es-AR'), type: '✗ Error', data: e.message, isError: true }])
    } finally { setRenameLoading(false) }
  }

  const sendNvsBackup = async () => {
    if (!decodedMac) return
    setNvsLoading(true)
    setCmdLog(prev => [...prev, { id: Date.now(), ts: new Date().toLocaleTimeString('es-AR'), type: '→ SAVE_BACKUP', data: {} }])
    try {
      const result = await adminApi.post('/admin/commands/config', { mac: decodedMac, config: { type: 'SAVE_CONFIG_BACKUP' } })
      if (!result?.success) throw new Error(result?.error || 'Backup failed')
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✓ Backup solicitado', data: 'OK' }])
    } catch (e) {
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✗ Error', data: e.message, isError: true }])
    } finally { setNvsLoading(false) }
  }

  const readPhTrimV = async () => {
    if (!decodedMac) return
    setPhReadLoading(true)
    setCmdLog(prev => [...prev, { id: Date.now(), ts: new Date().toLocaleTimeString('es-AR'), type: '→ PH_READ_TRIMV', data: {} }])
    try {
      const result = await adminApi.post('/admin/commands/raw', { mac: decodedMac, payload: { type: 'PH_READ_TRIMV' } })
      if (!result?.success) throw new Error(result?.error || 'Send failed')
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✓ Enviado', data: 'Esperando respuesta del dispositivo…' }])
    } catch (e) {
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✗ Error', data: e.message, isError: true }])
    } finally { setPhReadLoading(false) }
  }

  const sendPhCalibration = async () => {
    if (!decodedMac || !phCalV4 || !phCalV7 || !phCalV10) return
    const v4 = parseFloat(phCalV4), v7 = parseFloat(phCalV7), v10 = parseFloat(phCalV10)
    if (isNaN(v4) || isNaN(v7) || isNaN(v10)) return

    // Validar diferencia mínima entre puntos (50mV)
    if (Math.abs(v4 - v7) < 0.05 || Math.abs(v10 - v7) < 0.05 || Math.abs(v4 - v10) < 0.10) {
      window.alert('Los voltajes están demasiado juntos (mínimo 50mV entre puntos).\nVerificá que la sonda esté en el buffer correcto antes de leer.')
      return
    }

    // Detectar dirección de la sonda para mostrar en el confirm
    const isInverted = v4 < v7
    const dirNote = isInverted
      ? '⚠️ Sonda INVERTIDA detectada: V(pH4) < V(pH7) < V(pH9.18) — correcto para módulo GP-HIDRO'
      : '✓ Sonda estándar: V(pH4) > V(pH7) > V(pH9.18)'

    if (!window.confirm(`¿Guardar nueva calibración pH?\nV(pH4)=${v4.toFixed(4)} V, V(pH${phRefNeutral})=${v7.toFixed(4)} V, V(pH9.18)=${v10.toFixed(4)} V\n\n${dirNote}\n\nEsto sobreescribe la calibración en la memoria flash del dispositivo.`)) return
    setPhCalLoading(true)
    const payload = {
      type: 'SET_CONFIG',
      phCalV7: v7,
      phCalV4: v4,
      phCalV10: v10,
      phRefNeutral,
      phRefLow: 4.0,
      phRefHigh: 9.18,
    }
    setCmdLog(prev => [...prev, { id: Date.now(), ts: new Date().toLocaleTimeString('es-AR'), type: '→ SET_CONFIG (pH cal)', data: payload }])
    try {
      const result = await adminApi.post('/admin/commands/raw', { mac: decodedMac, payload })
      if (!result?.success) throw new Error(result?.error || 'Send failed')
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✓ Calibración enviada', data: 'Esperando confirmación del dispositivo…' }])
      setPhCalV4(''); setPhCalV7(''); setPhCalV10('')
    } catch (e) {
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✗ Error', data: e.message, isError: true }])
    } finally { setPhCalLoading(false) }
  }

  const sendSoilCalibration = async (useDefaults = false) => {
    if (!decodedMac) return
    const dry = useDefaults ? 4000 : parseInt(soilCalDryRaw)
    const wet = useDefaults ? 500 : parseInt(soilCalWetRaw)
    if (!useDefaults && (isNaN(dry) || isNaN(wet))) return
    if (!useDefaults && dry <= wet) {
      window.alert('dryRaw debe ser mayor que wetRaw (sensor ADC invertido: aire=alto, agua=bajo)')
      return
    }
    if (!useDefaults && ((dry - wet) < 200 || wet < 50 || wet > 3500 || dry < 1500 || dry > 4095)) {
      window.alert('Valores fuera de rango.\nwetRaw: 50–3500 | dryRaw: 1500–4095 | diferencia mínima: 200')
      return
    }
    const label = useDefaults ? 'valores de fábrica (dry=4000, wet=500)' : `dryRaw=${dry}, wetRaw=${wet}`
    if (!window.confirm(`¿Guardar calibración canal ${soilCalChannel}?\n${label}\n\nEsto sobreescribe la calibración en la memoria flash del dispositivo.`)) return
    setSoilCalLoading(true)
    const payload = { type: 'SET_CONFIG', channel: soilCalChannel, dryRaw: dry, wetRaw: wet }
    setCmdLog(prev => [...prev, { id: Date.now(), ts: new Date().toLocaleTimeString('es-AR'), type: `→ SET_CONFIG (suelo ch${soilCalChannel})`, data: payload }])
    try {
      const result = await adminApi.post('/admin/commands/raw', { mac: decodedMac, payload })
      if (!result?.success) throw new Error(result?.error || 'Send failed')
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✓ Calibración enviada', data: 'Esperando ACK del dispositivo…' }])
      if (!useDefaults) { setSoilCalDryRaw(''); setSoilCalWetRaw('') }
    } catch (e) {
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✗ Error', data: e.message, isError: true }])
    } finally { setSoilCalLoading(false) }
  }

  const openAssignModal = async () => {
    setShowAssignModal(true)
    setAssignSelectedUser(null)
    setAssignSelectedRoom(null)
    setAssignRooms([])
    setAssignSearch('')
    setAssignClientsLoading(true)
    try {
      const data = await adminApi.get('/admin/clients')
      const list = data?.clients || data || []
      setAssignClients(list)
    } catch (e) {
      console.error('Error fetching clients:', e)
    } finally {
      setAssignClientsLoading(false)
    }
  }

  const handleAssignUserSelect = async (client) => {
    setAssignSelectedUser(client)
    setAssignSelectedRoom(null)
    setAssignRooms([])
    const uid = client.userId || client.$id
    if (!uid) return
    setAssignRoomsLoading(true)
    try {
      const data = await adminApi.get(`/admin/clients/${uid}/rooms`)
      setAssignRooms(data?.rooms || [])
    } catch (e) {
      console.error('Error fetching client rooms:', e)
    } finally {
      setAssignRoomsLoading(false)
    }
  }

  const confirmAssign = async () => {
    if (!assignSelectedUser || !decodedMac) return
    setAssignLoading(true)
    try {
      const result = await adminApi.post(`/admin/devices/${encodeURIComponent(decodedMac)}/assign`, {
        userId: assignSelectedUser.userId || assignSelectedUser.$id,
        roomId: assignSelectedRoom?.$id || null,
      })
      if (!result?.success) throw new Error(result?.error || 'Assign failed')
      setShowAssignModal(false)
      // Reload device data
      adminApi.get(`/admin/devices/${encodeURIComponent(decodedMac)}`).then(data => {
        if (data && !data.error) setApiDevice(data)
      }).catch(() => { })
    } catch (e) {
      alert(`Error al asignar: ${e.message}`)
    } finally {
      setAssignLoading(false)
    }
  }

  const confirmUnassign = async () => {
    if (!decodedMac) return
    setUnassignLoading(true)
    try {
      const result = await adminApi.delete(`/admin/devices/${encodeURIComponent(decodedMac)}/assign`)
      if (!result?.success) throw new Error(result?.error || 'Unassign failed')
      setShowUnassignModal(false)
      adminApi.get(`/admin/devices/${encodeURIComponent(decodedMac)}`).then(data => {
        if (data && !data.error) setApiDevice(data)
      }).catch(() => { })
    } catch (e) {
      alert(`Error al desasignar: ${e.message}`)
    } finally {
      setUnassignLoading(false)
    }
  }

  const sendRawMqtt = async () => {
    if (!decodedMac || !rawPayload.trim()) return
    let parsed
    try { parsed = JSON.parse(rawPayload) } catch { setCmdLog(prev => [...prev, { id: Date.now(), ts: new Date().toLocaleTimeString('es-AR'), type: '✗ Error', data: 'JSON inválido', isError: true }]); return }
    setRawLoading(true)
    setCmdLog(prev => [...prev, { id: Date.now(), ts: new Date().toLocaleTimeString('es-AR'), type: '→ RAW_MQTT', data: parsed }])
    try {
      const result = await adminApi.post('/admin/commands/raw', { mac: decodedMac, payload: parsed })
      if (!result?.success) throw new Error(result?.error || 'Send failed')
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✓ Enviado', data: 'OK' }])
      setRawPayload('')
    } catch (e) {
      setCmdLog(prev => [...prev, { id: Date.now() + 1, ts: new Date().toLocaleTimeString('es-AR'), type: '✗ Error', data: e.message, isError: true }])
    } finally { setRawLoading(false) }
  }

  // ── Timeline helpers ──
  const formatLastSeen = (ts) => {
    if (!ts) return null
    const diff = Date.now() - ts
    if (diff < 60000) return 'hace menos de 1 min'
    const mins = Math.floor(diff / 60000)
    if (mins < 60) return `hace ${mins} min`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `hace ${hours}h`
    return `hace ${Math.floor(hours / 24)}d`
  }

  const timeToPercent = (timeStr) => {
    if (!timeStr) return 0
    const [h, m] = timeStr.split(':').map(Number)
    return ((h * 60 + (m || 0)) / 1440) * 100
  }
  const nowPct = (() => { const n = new Date(); return ((n.getHours() * 60 + n.getMinutes()) / 1440) * 100 })()

  if (apiLoading) {
    return (
      <div className="dd-page">
        <button className="dd-back" onClick={() => navigate('/devices')}><ArrowLeft size={18} /> Volver a dispositivos</button>
        <div className="dd-empty">Cargando dispositivo...</div>
      </div>
    )
  }

  if (!device) {
    return (
      <div className="dd-page">
        <button className="dd-back" onClick={() => navigate('/devices')}><ArrowLeft size={18} /> Volver a dispositivos</button>
        <div className="dd-empty">Dispositivo no encontrado: {decodedMac}</div>
      </div>
    )
  }

  return (
    <div className="dd-page">
      {/* Back + Header */}
      <button className="dd-back" onClick={() => navigate('/devices')}>
        <ArrowLeft size={18} /> Dispositivos
      </button>

      <div className="dd-header">
        <div className="dd-header-info">
          <div className="dd-header-row">
            <span className={`dd-dot ${device.isOnline ? 'online' : 'offline'}`} />
            <h1>{device.name || 'Sin nombre'}</h1>
            <span className="dd-model-badge">{device.model || '—'}</span>
            {(device.firmwareVersion || deviceStatus?.device?.firmware_version) && (
              <span className="dd-fw-badge">v{device.firmwareVersion || deviceStatus.device.firmware_version}</span>
            )}
            {device.isUnassigned && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 700,
                padding: '0.2rem 0.6rem', borderRadius: '9999px',
                background: 'rgba(167, 139, 250, 0.15)', color: '#c4b5fd',
                border: '1px solid rgba(167, 139, 250, 0.3)'
              }}>Sin asignar</span>
            )}
          </div>
          <div className="dd-header-sub">
            <span className="dd-mac">{decodedMac}</span>
            {!device.isOnline && device.liveData?.heartbeat?.ts && (
              <span className="dd-last-seen"><Clock size={11} /> {formatLastSeen(device.liveData.heartbeat.ts)}</span>
            )}
          </div>
        </div>
        <div className="dd-header-right">
          {device.isOnline && device.liveData && (
            <div className="dd-live-strip">
              {device.liveData.temperature != null && (
                <span><Thermometer size={13} /> {device.liveData.temperature?.toFixed(1)}°C</span>
              )}
              {device.liveData.humidity != null && (
                <span><Activity size={13} /> {device.liveData.humidity?.toFixed(0)}%</span>
              )}
              {device.liveData.ph != null && (
                <span><FlaskConical size={13} /> pH {Number(device.liveData.ph).toFixed(1)}</span>
              )}
              {device.liveData.tds != null && (
                <span><Zap size={13} /> {Math.round(device.liveData.tds)} ppm</span>
              )}
              {device.liveData.waterTemp != null && (
                <span><Thermometer size={13} /> 💧{Number(device.liveData.waterTemp).toFixed(1)}°C</span>
              )}
              {device.liveData.cameraReady != null && (
                <span><Camera size={13} /> {device.liveData.cameraReady ? '✓' : '✗'}</span>
              )}
              {device.liveData.totalCaptures != null && (
                <span><Camera size={13} /> {device.liveData.totalCaptures} fotos</span>
              )}
              {/* GP-SOIL: multi-channel or single moisture */}
              {Array.isArray(device.liveData.soilChannels) && device.liveData.soilChannels.some(v => v != null)
                ? device.liveData.soilChannels.map((v, i) => v != null && (
                  <span key={`soil-strip-${i}`} style={{ color: '#fcd34d' }}>
                    <Droplets size={13} style={{ color: '#b45309' }} />
                    {device.liveData.soilChannels.filter(x => x != null).length > 1 ? `S${i + 1}: ` : ''}{parseFloat(v).toFixed(1)}%
                  </span>
                ))
                : device.liveData.soil != null && (
                  <span style={{ color: '#fcd34d' }}><Droplets size={13} style={{ color: '#b45309' }} /> {parseFloat(device.liveData.soil).toFixed(1)}%</span>
                )
              }
              <span><Wifi size={13} /> {device.liveData.heartbeat?.rssi ?? device.liveData.rssi ?? '—'} dBm</span>
              <span><Clock size={13} /> {formatUptime(device.liveData.heartbeat?.uptime)}</span>
            </div>
          )}
          <div className="dd-header-actions">
            <button
              className="dd-hdr-btn status"
              onClick={() => sendCommand('STATUS')}
              disabled={cmdLoading || statusLoading}
              title="Solicitar estado"
            >
              <RefreshCw size={14} />
              <span>Estado</span>
            </button>
            <button
              className="dd-hdr-btn restart"
              onClick={() => sendCommand('RESTART')}
              disabled={cmdLoading}
              title="Reiniciar dispositivo"
            >
              <RotateCcw size={14} />
              <span>Reiniciar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="dd-tabs">
        {TABS.map(t => (
          <button key={t.key} className={`dd-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══ STATUS TAB ═══ */}
      {tab === 'status' && (
        <div className="dd-content">
          {/* Health summary bar */}
          {deviceStatus?.device && (
            <div className="dd-health-bar">
              {(() => {
                const rssi = deviceStatus.device.wifi_rssi || -100
                const sigInfo = getSignalInfo(rssi)
                const heap = device.liveData?.heartbeat?.heap
                const minHeap = device.liveData?.heartbeat?.minHeap
                const largestBlock = device.liveData?.heartbeat?.largestBlock
                const uptime = deviceStatus.device.uptime_seconds
                const resetCode = device.liveData?.resetReason ?? deviceStatus.device?.reset_reason
                const isCrash = [4, 5, 6, 7].includes(resetCode)
                const heapColor = minHeap
                  ? minHeap > 50000 ? '#10b981' : minHeap > 20000 ? '#f59e0b' : '#ef4444'
                  : heap ? '#94a3b8' : '#94a3b8'
                return (
                  <>
                    <div className="dd-health-item">
                      <Wifi size={13} style={{ color: sigInfo.color }} />
                      <span style={{ color: sigInfo.color }}>{rssi} dBm</span>
                      <span className="dd-health-label">{sigInfo.label}</span>
                    </div>
                    <div className="dd-health-sep" />
                    <div className="dd-health-item">
                      <Cpu size={13} style={{ color: heapColor }} />
                      <span style={{ color: heapColor }}>
                        {heap ? `${(heap / 1024).toFixed(0)} KB` : minHeap ? `min ${(minHeap / 1024).toFixed(0)} KB` : '— KB'}
                      </span>
                      <span className="dd-health-label">Heap libre</span>
                    </div>
                    {largestBlock != null && (
                      <>
                        <div className="dd-health-sep" />
                        <div className="dd-health-item">
                          <HardDrive size={13} style={{ color: largestBlock > 50000 ? '#10b981' : largestBlock > 20000 ? '#f59e0b' : '#ef4444' }} />
                          <span style={{ color: largestBlock > 50000 ? '#10b981' : largestBlock > 20000 ? '#f59e0b' : '#ef4444' }}>{(largestBlock / 1024).toFixed(0)} KB</span>
                          <span className="dd-health-label">Bloque max</span>
                        </div>
                      </>
                    )}
                    <div className="dd-health-sep" />
                    <div className="dd-health-item">
                      <Clock size={13} style={{ color: '#94a3b8' }} />
                      <span>{formatUptime(uptime)}</span>
                      <span className="dd-health-label">Uptime</span>
                    </div>
                    {resetCode != null && (
                      <>
                        <div className="dd-health-sep" />
                        <div className="dd-health-item">
                          <AlertTriangle size={13} style={{ color: isCrash ? '#ef4444' : '#10b981' }} />
                          <span style={{ color: isCrash ? '#ef4444' : '#10b981' }}>{getResetReasonLabel(resetCode)}</span>
                          <span className="dd-health-label">Último reset</span>
                        </div>
                      </>
                    )}
                    {device.isOnline && (
                      <>
                        <div className="dd-health-sep" />
                        <div className="dd-health-item">
                          <Heart size={13} style={{ color: '#10b981' }} />
                          <span style={{ color: '#10b981' }}>Online</span>
                          <span className="dd-health-label">Conectado</span>
                        </div>
                      </>
                    )}
                  </>
                )
              })()}
            </div>
          )}

          {/* Device info cards */}
          <div className="dd-info-grid">
            <div className="dd-info-card">
              <h3>Dispositivo</h3>
              <div className="dd-info-rows">
                <div className="dd-info-row"><span>Nombre</span><strong>{device.name || '—'}</strong></div>
                <div className="dd-info-row"><span>MAC</span><code>{decodedMac}</code></div>
                <div className="dd-info-row"><span>Modelo</span><strong>{device.model || '—'}</strong></div>
                <div className="dd-info-row"><span>Firmware</span><strong>{device.firmwareVersion || deviceStatus?.device?.firmware_version || '—'}</strong></div>
                <div className="dd-info-row"><span>Perfil HW</span><strong>{currentProfile || '—'}</strong></div>
                {deviceStatus?.device?.build_date && (
                  <div className="dd-info-row"><span>Build</span><strong>{deviceStatus.device.build_date} {deviceStatus.device.build_time || ''}</strong></div>
                )}
                <div className="dd-info-row"><span>Estado</span>
                  <span className={`dd-status-badge ${device.isOnline ? 'online' : 'offline'}`}>
                    {device.isOnline ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>
            </div>

            {device.growRoom && (
              <div className="dd-info-card">
                <h3>Sala de cultivo</h3>
                <div className="dd-info-rows">
                  <div className="dd-info-row"><span>Sala</span><strong>{device.growRoom.name || '—'}</strong></div>
                  <div className="dd-info-row"><span>Tipo</span><strong>{device.growRoom.cultivationType || '—'}</strong></div>
                  <div className="dd-info-row"><span>Activa</span><strong>{device.growRoom.active ? 'Sí' : 'No'}</strong></div>
                </div>
              </div>
            )}

            <div className="dd-info-card">
              <h3><User size={14} style={{ marginRight: 6, display: 'inline' }} />Usuario asignado</h3>
              {device.authUser ? (
                <div className="dd-info-rows">
                  <div className="dd-info-row"><span>Nombre</span><strong>{device.authUser.name || '—'}</strong></div>
                  <div className="dd-info-row"><span>Email</span><strong>{device.authUser.email || '—'}</strong></div>
                  {device.userId && (
                    <div className="dd-info-row"><span>ID</span><code style={{ fontSize: '0.7rem' }}>{device.userId}</code></div>
                  )}
                  <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="dd-cmd-btn config"
                      onClick={openAssignModal}
                      style={{ flex: 1, justifyContent: 'center' }}
                    >
                      <UserPlus size={14} /> Reasignar
                    </button>
                    <button
                      className="dd-cmd-btn danger"
                      onClick={() => setShowUnassignModal(true)}
                      style={{ flex: 1, justifyContent: 'center' }}
                    >
                      <UserMinus size={14} /> Desasignar
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '0.5rem 0' }}>
                  <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.75rem' }}>Sin asignar</div>
                  <button
                    className="dd-cmd-btn config"
                    onClick={openAssignModal}
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    <UserPlus size={14} /> Asignar a usuario
                  </button>
                </div>
              )}
            </div>

            {deviceStatus?.device && (
              <div className="dd-info-card">
                <h3>Telemetría en vivo</h3>
                <div className="dd-info-rows">
                  {deviceStatus.sensor?.t != null && (
                    <div className="dd-info-row"><span>Temperatura</span><strong>{deviceStatus.sensor.t.toFixed(1)}°C</strong></div>
                  )}
                  {deviceStatus.sensor?.h != null && (
                    <div className="dd-info-row"><span>Humedad</span><strong>{deviceStatus.sensor.h.toFixed(0)}%</strong></div>
                  )}
                  {deviceStatus.sensor?.d != null && (
                    <div className="dd-info-row"><span>DPV</span><strong>{deviceStatus.sensor.d.toFixed(2)} kPa</strong></div>
                  )}
                  {device.liveData?.ph != null && (
                    <div className="dd-info-row"><span>pH</span><strong style={{ color: '#22c55e' }}>{Number(device.liveData.ph).toFixed(1)}</strong></div>
                  )}
                  {device.liveData?.tds != null && (
                    <div className="dd-info-row"><span>TDS</span><strong style={{ color: '#f59e0b' }}>{Math.round(device.liveData.tds)} ppm</strong></div>
                  )}
                  {device.liveData?.waterTemp != null && (
                    <div className="dd-info-row"><span>Temp. Agua</span><strong style={{ color: '#06b6d4' }}>{Number(device.liveData.waterTemp).toFixed(1)}°C</strong></div>
                  )}
                  {device.liveData?.cameraReady != null && (
                    <div className="dd-info-row"><span>Cámara</span><strong style={{ color: device.liveData.cameraReady ? '#22c55e' : '#ef4444' }}>{device.liveData.cameraReady ? '✓ Lista' : '✗ Error'}</strong></div>
                  )}
                  {device.liveData?.timelapseActive != null && (
                    <div className="dd-info-row"><span>Timelapse</span><strong>{device.liveData.timelapseActive ? 'Activo' : 'Inactivo'}</strong></div>
                  )}
                  {device.liveData?.totalCaptures != null && (
                    <div className="dd-info-row"><span>Capturas</span><strong>{device.liveData.totalCaptures}{device.liveData.failedCaptures ? ` (${device.liveData.failedCaptures} fallidas)` : ''}</strong></div>
                  )}
                  {device.liveData?.pendingUploads != null && device.liveData.pendingUploads > 0 && (
                    <div className="dd-info-row"><span>Pendientes</span><strong style={{ color: '#f59e0b' }}>{device.liveData.pendingUploads}</strong></div>
                  )}
                  {/* GP-SOIL: soil moisture channels */}
                  {Array.isArray(device.liveData?.soilChannels) && device.liveData.soilChannels.some(v => v != null)
                    ? device.liveData.soilChannels.map((v, i) => v != null && (
                      <div key={`soil-row-${i}`} className="dd-info-row">
                        <span>{device.liveData.soilChannels.filter(x => x != null).length > 1 ? `Suelo Ch${i + 1}` : 'Suelo'}</span>
                        <strong style={{ color: '#fcd34d' }}>{parseFloat(v).toFixed(1)}%</strong>
                      </div>
                    ))
                    : device.liveData?.soil != null && (
                      <div className="dd-info-row"><span>Suelo</span><strong style={{ color: '#fcd34d' }}>{parseFloat(device.liveData.soil).toFixed(1)}%</strong></div>
                    )
                  }
                  {/* 💡 GP-DIM: intensidad, voltaje DIM+, calibración */}
                  {device.liveData?.dimLevel != null && (() => {
                    const DIM_DIAG = { 0: '—', 1: 'OK', 2: '⚠ Sin driver', 3: '⚠ Corto DIM', 4: '⚠ Sobretensión' };
                    const diagState = device.liveData.dimDiagState ?? 0;
                    const diagLabel = DIM_DIAG[diagState] ?? `Estado ${diagState}`;
                    const diagColor = diagState === 1 ? '#10b981' : diagState > 1 ? '#ef4444' : '#6b7280';
                    const voltX10 = device.liveData.dimVoltX10;
                    // Voltaje esperado para el nivel actual (protocolo 1-10V): Vexp = 1 + nivel/100 * 9
                    const dimLvl = device.liveData.dimLevel;
                    const vExpected = dimLvl > 0 ? (1 + dimLvl / 100 * 9) : 0;
                    const vMeasured = voltX10 != null ? voltX10 / 10 : null;
                    const vDelta = vMeasured != null && dimLvl > 0 ? Math.abs(vMeasured - vExpected) : null;
                    const vColor = vDelta == null ? '#6b7280' : vDelta < 0.5 ? '#10b981' : vDelta < 1.5 ? '#f59e0b' : '#ef4444';
                    const sched = device.liveData.dimSchedule;
                    return (<>
                      <div className="dd-info-row">
                        <span>Intensidad</span>
                        <strong style={{ color: dimLvl === 0 ? '#6b7280' : '#fbbf24' }}>{dimLvl}%</strong>
                      </div>
                      {vMeasured != null && (
                        <div className="dd-info-row">
                          <span>Voltaje DIM+</span>
                          <strong style={{ color: vColor }} title={`Esperado ~${vExpected.toFixed(1)}V | Delta: ${vDelta != null ? vDelta.toFixed(2) : '—'}V`}>
                            {vMeasured.toFixed(1)} V {vDelta != null && vDelta > 1.5 ? '⚠' : ''}
                          </strong>
                        </div>
                      )}
                      {diagState !== 0 && (
                        <div className="dd-info-row">
                          <span>Diagnóstico DIM</span>
                          <strong style={{ color: diagColor }}>{diagLabel}</strong>
                        </div>
                      )}
                      {device.liveData.dimPwmTop != null && (
                        <div className="dd-info-row">
                          <span>Duty máx. (PWM)</span>
                          <strong style={{ color: '#94a3b8' }}>{device.liveData.dimPwmTop}</strong>
                        </div>
                      )}
                      {device.liveData.dimGamma != null && (
                        <div className="dd-info-row">
                          <span>Gamma</span>
                          <strong style={{ color: '#94a3b8' }}>γ = {Number(device.liveData.dimGamma).toFixed(1)}</strong>
                        </div>
                      )}
                      {sched?.en && (
                        <div className="dd-info-row">
                          <span>Horario dimmer</span>
                          <strong style={{ color: '#818cf8' }}>
                            {`${String(Math.floor((sched.on ?? 480) / 60)).padStart(2,'0')}:${String((sched.on ?? 480) % 60).padStart(2,'0')} → ${String(Math.floor((sched.of ?? 1200) / 60)).padStart(2,'0')}:${String((sched.of ?? 1200) % 60).padStart(2,'0')} @${sched.ol ?? 100}%`}
                          </strong>
                        </div>
                      )}
                      {device.liveData.manualOverride === 1 && (
                        <div className="dd-info-row">
                          <span>Override manual</span>
                          <strong style={{ color: '#f59e0b' }}>Activo (15 min)</strong>
                        </div>
                      )}
                    </>);
                  })()}
                  <div className="dd-info-row">
                    <span>WiFi</span>
                    <strong style={{ color: getSignalInfo(deviceStatus.device.wifi_rssi || -100).color }}>
                      {deviceStatus.device.wifi_rssi || '—'} dBm ({getSignalInfo(deviceStatus.device.wifi_rssi || -100).label})
                    </strong>
                  </div>
                  <div className="dd-info-row"><span>Uptime</span><strong>{formatUptime(deviceStatus.device.uptime_seconds)}</strong></div>
                  <div className="dd-info-row">
                    <span>Heap</span>
                    <strong>
                      {device.liveData?.heartbeat?.heap
                        ? `${(device.liveData.heartbeat.heap / 1024).toFixed(0)} KB`
                        : device.liveData?.hpAtConnect ?? device.hpAtConnect
                          ? <span title="Heap al momento de conectar (antes de config recovery)">
                            {((device.liveData?.hpAtConnect ?? device.hpAtConnect) / 1024).toFixed(0)} KB <span style={{ fontSize: '10px', opacity: 0.7 }}>(boot)</span>
                          </span>
                          : '—'}
                    </strong>
                  </div>
                  {device.liveData?.heartbeat?.minHeap != null && (
                    <div className="dd-info-row">
                      <span>Min Heap</span>
                      <strong style={{ color: device.liveData.heartbeat.minHeap > 50000 ? '#10b981' : device.liveData.heartbeat.minHeap > 20000 ? '#f59e0b' : '#ef4444' }}>
                        {(device.liveData.heartbeat.minHeap / 1024).toFixed(0)} KB
                      </strong>
                    </div>
                  )}
                  {device.liveData?.heartbeat?.largestBlock != null && (
                    <div className="dd-info-row">
                      <span>Bloque Max</span>
                      <strong style={{ color: device.liveData.heartbeat.largestBlock > 50000 ? '#10b981' : device.liveData.heartbeat.largestBlock > 20000 ? '#f59e0b' : '#ef4444' }}>
                        {(device.liveData.heartbeat.largestBlock / 1024).toFixed(0)} KB
                      </strong>
                    </div>
                  )}
                  {device.liveData?.rtcTemp != null && (
                    <div className="dd-info-row">
                      <span>Temp. RTC</span>
                      <strong style={{ color: device.liveData.rtcTemp > 55 ? '#ef4444' : device.liveData.rtcTemp > 45 ? '#f59e0b' : '#10b981' }}>
                        {device.liveData.rtcTemp.toFixed(1)}°C {device.liveData.rtcTemp > 55 ? '🔴' : device.liveData.rtcTemp > 45 ? '🟡' : ''}
                      </strong>
                    </div>
                  )}
                  {device.liveData?.powerCycles != null && (
                    <div className="dd-info-row">
                      <span>Power Cycles</span>
                      <strong>{device.liveData.powerCycles}</strong>
                    </div>
                  )}
                  {device.liveData?.uptimeHours != null && (
                    <div className="dd-info-row">
                      <span>Uptime Total</span>
                      <strong>{device.liveData.uptimeHours >= 24 ? `${Math.floor(device.liveData.uptimeHours / 24)}d ${device.liveData.uptimeHours % 24}h` : `${device.liveData.uptimeHours}h`}</strong>
                    </div>
                  )}
                  {device.liveData?.mqttFailures != null && device.liveData.mqttFailures > 0 && (
                    <div className="dd-info-row">
                      <span>Fallas MQTT</span>
                      <strong style={{ color: device.liveData.mqttFailures > 10 ? '#ef4444' : '#f59e0b' }}>{device.liveData.mqttFailures}</strong>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ═══ GP-BOX / GP-MINI: Ambiente ═══ */}
          {(device.model === 'GP-BOX' || device.model === 'GP-MINI') && deviceStatus?.sensor && (() => {
            const s = deviceStatus.sensor
            const ld = device.liveData
            const getDpvColor = (dpv) => {
              if (dpv == null) return '#94a3b8'
              if (dpv >= 0.8 && dpv <= 1.2) return '#10b981'  // óptimo
              if (dpv >= 0.4 && dpv <= 1.6) return '#f59e0b'  // aceptable
              return '#ef4444'                                  // crítico
            }
            const getHumColor = (h) => {
              if (h == null) return '#94a3b8'
              if (h >= 50 && h <= 70) return '#10b981'
              if (h >= 40 && h <= 80) return '#f59e0b'
              return '#ef4444'
            }
            const getTempColor = (t) => {
              if (t == null) return '#94a3b8'
              if (t >= 22 && t <= 28) return '#10b981'
              if (t >= 18 && t <= 32) return '#f59e0b'
              return '#ef4444'
            }
            return (
              <div className="dd-section">
                <h2 className="dd-section-title"><Leaf size={16} /> Ambiente — {device.model}</h2>
                <div className="dd-device-metrics">
                  <div className="dd-metric-card">
                    <div className="dd-metric-icon" style={{ color: getTempColor(s.t ?? ld?.temperature) }}><Thermometer size={22} /></div>
                    <div className="dd-metric-value" style={{ color: getTempColor(s.t ?? ld?.temperature) }}>{(s.t ?? ld?.temperature)?.toFixed(1) ?? '—'}°C</div>
                    <div className="dd-metric-label">Temperatura</div>
                    <div className="dd-metric-sub" style={{ color: getTempColor(s.t ?? ld?.temperature) }}>
                      {(s.t ?? ld?.temperature) != null ? ((s.t ?? ld?.temperature) >= 22 && (s.t ?? ld?.temperature) <= 28 ? '✓ Óptima' : (s.t ?? ld?.temperature) >= 18 && (s.t ?? ld?.temperature) <= 32 ? '⚠ Aceptable' : '✗ Crítica') : '—'}
                    </div>
                  </div>
                  <div className="dd-metric-card">
                    <div className="dd-metric-icon" style={{ color: getHumColor(s.h ?? ld?.humidity) }}><Droplets size={22} /></div>
                    <div className="dd-metric-value" style={{ color: getHumColor(s.h ?? ld?.humidity) }}>{(s.h ?? ld?.humidity)?.toFixed(0) ?? '—'}%</div>
                    <div className="dd-metric-label">Humedad</div>
                    <div className="dd-metric-sub" style={{ color: getHumColor(s.h ?? ld?.humidity) }}>
                      {(s.h ?? ld?.humidity) != null ? ((s.h ?? ld?.humidity) >= 50 && (s.h ?? ld?.humidity) <= 70 ? '✓ Óptima' : (s.h ?? ld?.humidity) >= 40 && (s.h ?? ld?.humidity) <= 80 ? '⚠ Aceptable' : '✗ Crítica') : '—'}
                    </div>
                  </div>
                  <div className="dd-metric-card">
                    <div className="dd-metric-icon" style={{ color: getDpvColor(s.d ?? ld?.dpv) }}><Wind size={22} /></div>
                    <div className="dd-metric-value" style={{ color: getDpvColor(s.d ?? ld?.dpv) }}>{(s.d ?? ld?.dpv)?.toFixed(2) ?? '—'} kPa</div>
                    <div className="dd-metric-label">DPV</div>
                    <div className="dd-metric-sub" style={{ color: getDpvColor(s.d ?? ld?.dpv) }}>
                      {(s.d ?? ld?.dpv) != null ? ((s.d ?? ld?.dpv) >= 0.8 && (s.d ?? ld?.dpv) <= 1.2 ? '✓ Óptimo' : (s.d ?? ld?.dpv) >= 0.4 && (s.d ?? ld?.dpv) <= 1.6 ? '⚠ Aceptable' : '✗ Crítico') : '—'}
                    </div>
                  </div>
                  <div className="dd-metric-card">
                    <div className="dd-metric-icon" style={{ color: '#94a3b8' }}><Activity size={22} /></div>
                    <div className="dd-metric-value">{s.sn ?? ld?.sensorName ?? '—'}</div>
                    <div className="dd-metric-label">Sensor</div>
                    <div className="dd-metric-sub" style={{ color: s.working !== false ? '#10b981' : '#ef4444' }}>{s.working !== false ? '✓ Funcionando' : '✗ Error'}</div>
                  </div>
                </div>

                {/* Diagnóstico: picos/congelamientos descartados desde el último reinicio (fw ≥ 8.4.9) */}
                {(s.spikes != null || s.frozenTH != null || s.frozenCO2 != null) && (
                  <div className="dd-info-rows" style={{ marginTop: '1rem' }}>
                    {s.spikes != null && (
                      <div className="dd-info-row">
                        <span title="Lecturas del AHT10 descartadas por salto brusco (glitch de I2C) desde el último reinicio">
                          <AlertTriangle size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />Picos AHT descartados
                        </span>
                        <strong style={{ color: s.spikes > 0 ? '#f59e0b' : '#94a3b8' }}>{s.spikes}</strong>
                      </div>
                    )}
                    {s.frozenTH != null && (
                      <div className="dd-info-row">
                        <span title="Veces que el AHT10 quedó clavado en el mismo valor y se forzó un recovery">
                          <RefreshCw size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />Recovery T/H (congelado)
                        </span>
                        <strong style={{ color: s.frozenTH > 0 ? '#ef4444' : '#94a3b8' }}>{s.frozenTH}</strong>
                      </div>
                    )}
                    {s.frozenCO2 != null && (
                      <div className="dd-info-row">
                        <span title="Veces que el SCD4x quedó clavado en el mismo ppm y se forzó un recovery">
                          <RefreshCw size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />Recovery CO2 (congelado)
                        </span>
                        <strong style={{ color: s.frozenCO2 > 0 ? '#ef4444' : '#94a3b8' }}>{s.frozenCO2}</strong>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })()}

          {/* ═══ GP-MINI-HUB: Salud agregada de sensores (todos los canales) ═══ */}
          {device.model === 'GP-MINI-HUB' && (deviceStatus?.spikes != null || deviceStatus?.frozenTH != null || deviceStatus?.frozenCO2 != null) && (
            <div className="dd-section">
              <h2 className="dd-section-title"><Activity size={16} /> Salud de sensores — Hub</h2>
              <div className="dd-info-rows">
                {deviceStatus.spikes != null && (
                  <div className="dd-info-row">
                    <span title="Lecturas AHT10 descartadas por salto brusco en cualquier canal desde el último reinicio">
                      <AlertTriangle size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />Picos AHT descartados (todos los canales)
                    </span>
                    <strong style={{ color: deviceStatus.spikes > 0 ? '#f59e0b' : '#94a3b8' }}>{deviceStatus.spikes}</strong>
                  </div>
                )}
                {deviceStatus.frozenTH != null && (
                  <div className="dd-info-row">
                    <span title="Recoveries forzados por AHT10 clavado en cualquier canal">
                      <RefreshCw size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />Recovery T/H (congelado)
                    </span>
                    <strong style={{ color: deviceStatus.frozenTH > 0 ? '#ef4444' : '#94a3b8' }}>{deviceStatus.frozenTH}</strong>
                  </div>
                )}
                {deviceStatus.frozenCO2 != null && (
                  <div className="dd-info-row">
                    <span title="Recoveries forzados por SCD4x clavado en cualquier canal">
                      <RefreshCw size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />Recovery CO2 (congelado)
                    </span>
                    <strong style={{ color: deviceStatus.frozenCO2 > 0 ? '#ef4444' : '#94a3b8' }}>{deviceStatus.frozenCO2}</strong>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ═══ GP-HIDRO: Sensores hidropónicos ═══ */}
          {(device.model?.includes('HIDRO') || deviceStatus?.sensor?.type === 'hydro') && deviceStatus?.sensor && (() => {
            const s = deviceStatus.sensor
            const ld = device.liveData
            const getPhColor = (ph) => {
              if (ph == null) return '#94a3b8'
              if (ph >= 5.5 && ph <= 6.5) return '#10b981'
              if (ph >= 5.0 && ph <= 7.0) return '#f59e0b'
              return '#ef4444'
            }
            const getTdsColor = (tds) => {
              if (tds == null) return '#94a3b8'
              if (tds >= 800 && tds <= 1800) return '#10b981'
              if (tds >= 400 && tds <= 2400) return '#f59e0b'
              return '#ef4444'
            }
            const getWtColor = (wt) => {
              if (wt == null) return '#94a3b8'
              if (wt >= 18 && wt <= 24) return '#10b981'
              if (wt >= 15 && wt <= 28) return '#f59e0b'
              return '#ef4444'
            }
            const ph = s.ph ?? ld?.ph
            const tds = s.tds ?? ld?.tds
            const wt = s.waterTemp ?? ld?.waterTemp
            return (
              <div className="dd-section">
                <h2 className="dd-section-title"><FlaskConical size={16} /> Sensores Hidropónicos</h2>
                <div className="dd-device-metrics">
                  <div className="dd-metric-card">
                    <div className="dd-metric-icon" style={{ color: getPhColor(ph) }}><FlaskConical size={22} /></div>
                    <div className="dd-metric-value" style={{ color: getPhColor(ph) }}>{ph != null ? Number(ph).toFixed(2) : '—'}</div>
                    <div className="dd-metric-label">pH</div>
                    <div className="dd-metric-sub" style={{ color: getPhColor(ph) }}>
                      {ph != null ? (ph >= 5.5 && ph <= 6.5 ? '✓ Óptimo' : ph >= 5.0 && ph <= 7.0 ? '⚠ Aceptable' : '✗ Crítico') : s.phWorking === false ? '✗ Sin sensor' : '—'}
                    </div>
                  </div>
                  <div className="dd-metric-card">
                    <div className="dd-metric-icon" style={{ color: getTdsColor(tds) }}><Zap size={22} /></div>
                    <div className="dd-metric-value" style={{ color: getTdsColor(tds) }}>{tds != null ? Math.round(tds) : '—'} <span style={{ fontSize: '0.75rem' }}>ppm</span></div>
                    <div className="dd-metric-label">TDS / EC</div>
                    <div className="dd-metric-sub" style={{ color: getTdsColor(tds) }}>
                      {tds != null ? (tds >= 800 && tds <= 1800 ? '✓ Óptimo' : tds >= 400 && tds <= 2400 ? '⚠ Aceptable' : '✗ Crítico') : s.tdsWorking === false ? '✗ Sin sensor' : '—'}
                    </div>
                  </div>
                  <div className="dd-metric-card">
                    <div className="dd-metric-icon" style={{ color: getWtColor(wt) }}><Droplets size={22} /></div>
                    <div className="dd-metric-value" style={{ color: getWtColor(wt) }}>{wt != null ? Number(wt).toFixed(1) : '—'}°C</div>
                    <div className="dd-metric-label">Temp. Agua</div>
                    <div className="dd-metric-sub" style={{ color: getWtColor(wt) }}>
                      {wt != null ? (wt >= 18 && wt <= 24 ? '✓ Óptima' : wt >= 15 && wt <= 28 ? '⚠ Aceptable' : '✗ Crítica') : s.waterTempWorking === false ? '✗ Sin sensor' : '—'}
                    </div>
                  </div>
                  <div className="dd-metric-card">
                    <div className="dd-metric-icon" style={{ color: s.calibrated ? '#10b981' : '#f59e0b' }}><Settings size={22} /></div>
                    <div className="dd-metric-value" style={{ fontSize: '0.9rem', color: s.calibrated ? '#10b981' : '#f59e0b' }}>{s.calibrated ? 'Calibrado' : 'Sin cal.'}</div>
                    <div className="dd-metric-label">Calibración</div>
                    <div className="dd-metric-sub">{s.trimV != null ? `TrimV: ${Number(s.trimV).toFixed(4)}V` : '—'}</div>
                  </div>
                </div>
                {s.calibrated && (
                  <div className="dd-info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginTop: '1rem' }}>
                    <div className="dd-info-card">
                      <h3>Curva ácida (pH 4 ↔ 7)</h3>
                      <div className="dd-info-rows">
                        <div className="dd-info-row"><span>V @ pH 4.0</span><strong>{s.calV4?.toFixed(6)} V</strong></div>
                        <div className="dd-info-row"><span>V @ pH 7.0</span><strong>{s.calV7?.toFixed(6)} V</strong></div>
                        <div className="dd-info-row"><span>Slope</span><strong>{s.slopeAcid?.toFixed(4)}</strong></div>
                        <div className="dd-info-row"><span>Offset</span><strong>{s.offsetAcid?.toFixed(4)}</strong></div>
                      </div>
                    </div>
                    <div className="dd-info-card">
                      <h3>Curva alcalina (pH 7 ↔ 9.18)</h3>
                      <div className="dd-info-rows">
                        <div className="dd-info-row"><span>V @ pH 9.18</span><strong>{s.calV10?.toFixed(6)} V</strong></div>
                        <div className="dd-info-row"><span>V @ pH 7.0</span><strong>{s.calV7?.toFixed(6)} V</strong></div>
                        <div className="dd-info-row"><span>Slope</span><strong>{s.slopeAlk?.toFixed(4)}</strong></div>
                        <div className="dd-info-row"><span>Offset</span><strong>{s.offsetAlk?.toFixed(4)}</strong></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* FSM diagnostic — only when STATUS has been fetched */}
                {(s.phReadOk != null || s.tdsReadOk != null || s.phAge != null) && (
                  <div className="dd-info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginTop: '1rem' }}>
                    <div className="dd-info-card">
                      <h3><Activity size={14} style={{ marginRight: 6 }} />Estado de sensores (FSM)</h3>
                      <div className="dd-info-rows">
                        <div className="dd-info-row">
                          <span>pH</span>
                          <strong style={{ color: s.phReadOk ? '#10b981' : '#ef4444' }}>
                            {s.phReadOk ? '✓ Lectura válida' : '✗ Sin lectura'}{s.phStuck ? ' — ⚠ BLOQUEADO' : ''}
                          </strong>
                        </div>
                        <div className="dd-info-row">
                          <span>TDS</span>
                          <strong style={{ color: s.tdsReadOk ? '#10b981' : '#ef4444' }}>
                            {s.tdsReadOk ? '✓ Lectura válida' : '✗ Sin lectura'}{s.tdsComp ? ' (T° comp)' : ''}
                          </strong>
                        </div>
                        <div className="dd-info-row">
                          <span>Temp. agua</span>
                          <strong style={{ color: s.wtReadOk ? '#10b981' : '#ef4444' }}>
                            {s.wtReadOk ? '✓ Lectura válida' : '✗ Sin lectura'}
                          </strong>
                        </div>
                        {s.busy != null && (
                          <div className="dd-info-row">
                            <span>FSM</span>
                            <strong style={{ color: s.busy ? '#f59e0b' : '#10b981' }}>
                              {s.busy ? '⏳ Midiendo…' : '✓ Idle'}
                            </strong>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="dd-info-card">
                      <h3><Clock size={14} style={{ marginRight: 6 }} />Antigüedad de datos</h3>
                      <div className="dd-info-rows">
                        {s.phAge != null && (
                          <div className="dd-info-row">
                            <span>Último pH</span>
                            <strong style={{ color: s.phAge > 300 ? '#f59e0b' : '#10b981' }}>
                              {s.phAge < 60 ? `${s.phAge}s` : `${Math.floor(s.phAge / 60)}m ${s.phAge % 60}s`}
                            </strong>
                          </div>
                        )}
                        {s.tdsAge != null && (
                          <div className="dd-info-row">
                            <span>Último TDS</span>
                            <strong style={{ color: s.tdsAge > 600 ? '#f59e0b' : '#10b981' }}>
                              {s.tdsAge < 60 ? `${s.tdsAge}s` : `${Math.floor(s.tdsAge / 60)}m ${s.tdsAge % 60}s`}
                            </strong>
                          </div>
                        )}
                        {s.wtAge != null && (
                          <div className="dd-info-row">
                            <span>Última T° agua</span>
                            <strong style={{ color: s.wtAge > 300 ? '#f59e0b' : '#10b981' }}>
                              {s.wtAge < 60 ? `${s.wtAge}s` : `${Math.floor(s.wtAge / 60)}m ${s.wtAge % 60}s`}
                            </strong>
                          </div>
                        )}
                        {s.nextPhMs != null && (
                          <div className="dd-info-row">
                            <span>Próx. pH en</span>
                            <strong style={{ color: '#94a3b8' }}>{Math.max(0, Math.round(s.nextPhMs / 1000))}s</strong>
                          </div>
                        )}
                        {s.nextTdsMs != null && (
                          <div className="dd-info-row">
                            <span>Próx. TDS en</span>
                            <strong style={{ color: '#94a3b8' }}>{Math.max(0, Math.round(s.nextTdsMs / 1000))}s</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })()}

          {/* ═══ GP-SOIL: Sensores de humedad de suelo ═══ */}
          {(device.model === 'GP-SOIL' || deviceStatus?.sensor?.soilChannels != null || deviceStatus?.sensor?.soil != null) && (() => {
            const s = deviceStatus?.sensor
            const ld = device.liveData
            const channels = s?.soilChannels ?? (ld?.soilChannels) ?? (s?.soil != null ? [s.soil] : ld?.soil != null ? [ld.soil] : [])
            const rawChannels = s?.soilRawChannels ?? ld?.soilRawChannels ?? []
            const statusChannels = s?.soilStatusChannels ?? ld?.soilStatusChannels ?? []
            const validChannels = channels.filter(v => v != null)
            if (validChannels.length === 0 && s?.soil == null && ld?.soil == null) return null

            const getSoilColor = (pct) => {
              if (pct == null) return '#94a3b8'
              if (pct >= 40 && pct <= 75) return '#10b981'
              if (pct >= 20 && pct <= 85) return '#f59e0b'
              return '#ef4444'
            }
            const getSoilLabel = (pct) => {
              if (pct == null) return '—'
              if (pct < 15) return '✗ Seco'
              if (pct < 40) return '⚠ Bajo'
              if (pct <= 75) return '✓ Óptimo'
              if (pct <= 90) return '⚠ Alto'
              return '✗ Saturado'
            }
            return (
              <div className="dd-section">
                <h2 className="dd-section-title"><Droplets size={16} /> Humedad de Suelo</h2>
                <div className="dd-device-metrics">
                  {channels.map((v, i) => (
                    <div key={`soil-card-${i}`} className="dd-metric-card">
                      <div className="dd-metric-icon" style={{ color: getSoilColor(v) }}><Droplets size={22} /></div>
                      <div className="dd-metric-value" style={{ color: getSoilColor(v) }}>{v != null ? parseFloat(v).toFixed(1) : '—'}<span style={{ fontSize: '0.75rem' }}>%</span></div>
                      <div className="dd-metric-label">{channels.length > 1 ? `Canal ${i + 1}` : 'Humedad'}</div>
                      <div className="dd-metric-sub" style={{ color: getSoilColor(v) }}>{getSoilLabel(v)}</div>
                    </div>
                  ))}
                  {s?.channels != null && (
                    <div className="dd-metric-card">
                      <div className="dd-metric-icon" style={{ color: '#6366f1' }}><Activity size={22} /></div>
                      <div className="dd-metric-value" style={{ color: '#6366f1' }}>{s.channels}</div>
                      <div className="dd-metric-label">Canales</div>
                      <div className="dd-metric-sub" style={{ color: validChannels.length === s.channels ? '#10b981' : '#f59e0b' }}>
                        {validChannels.length}/{s.channels} activos
                      </div>
                    </div>
                  )}
                </div>
                {/* Per-channel detail table */}
                {channels.length > 0 && (rawChannels.length > 0 || statusChannels.length > 0) && (
                  <div className="dd-info-card" style={{ marginTop: '1rem' }}>
                    <h3><Activity size={14} style={{ marginRight: 6 }} />Detalle por canal</h3>
                    <div className="dd-info-rows">
                      {channels.map((v, i) => (
                        <div key={`soil-detail-${i}`} className="dd-info-row">
                          <span>Canal {i + 1}</span>
                          <strong style={{ color: getSoilColor(v), display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <span>{v != null ? `${parseFloat(v).toFixed(1)}%` : '—'}</span>
                            {rawChannels[i] != null && <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.8rem' }}>raw: {rawChannels[i]}</span>}
                            {statusChannels[i] && <span style={{ color: statusChannels[i] === 'ok' ? '#10b981' : statusChannels[i] === 'dry' ? '#f59e0b' : '#ef4444', fontSize: '0.8rem' }}>{statusChannels[i]}</span>}
                          </strong>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })()}

          {/* ═══ GP-CAM: Estado de cámara ═══ */}
          {(device.model === 'GP-CAM' || deviceStatus?.sensor?.type === 'camera') && (() => {
            const s = deviceStatus?.sensor
            const ld = device.liveData
            const cr = s?.working ?? ld?.cameraReady
            const active = s?.timelapseActive ?? ld?.timelapseActive
            const total = s?.totalCaptures ?? ld?.totalCaptures
            const failed = s?.failedCaptures ?? ld?.failedCaptures
            const interval = s?.interval ?? ld?.interval
            const pending = ld?.pendingUploads
            const FRAME_SIZES = {
              0: '96x96', 1: 'QQVGA 160x120', 2: 'QCIF 176x144', 3: 'HQVGA 240x176',
              4: '240x240', 5: 'QVGA 320x240', 6: 'CIF 400x296', 7: 'HVGA 480x320',
              8: 'VGA 640x480', 9: 'SVGA 800x600', 10: 'XGA 1024x768', 11: 'HD 1280x720',
              12: 'SXGA 1280x1024', 13: 'UXGA 1600x1200'
            }
            return (
              <div className="dd-section">
                <h2 className="dd-section-title"><Camera size={16} /> Estado de Cámara</h2>
                <div className="dd-device-metrics">
                  <div className="dd-metric-card">
                    <div className="dd-metric-icon" style={{ color: cr ? '#10b981' : '#ef4444' }}><Camera size={22} /></div>
                    <div className="dd-metric-value" style={{ color: cr ? '#10b981' : '#ef4444' }}>{cr ? '✓' : '✗'}</div>
                    <div className="dd-metric-label">Cámara</div>
                    <div className="dd-metric-sub" style={{ color: cr ? '#10b981' : '#ef4444' }}>{cr ? 'Lista' : 'Error / Sin init'}</div>
                  </div>
                  <div className="dd-metric-card">
                    <div className="dd-metric-icon" style={{ color: active ? '#10b981' : '#94a3b8' }}><Activity size={22} /></div>
                    <div className="dd-metric-value" style={{ color: active ? '#10b981' : '#94a3b8' }}>{active ? 'Activo' : 'Inactivo'}</div>
                    <div className="dd-metric-label">Timelapse</div>
                    <div className="dd-metric-sub">{interval ? `Intervalo: ${interval >= 3600 ? `${Math.floor(interval / 3600)}h` : interval >= 60 ? `${Math.floor(interval / 60)}m` : `${interval}s`}` : '—'}</div>
                  </div>
                  <div className="dd-metric-card">
                    <div className="dd-metric-icon" style={{ color: '#6366f1' }}><Image size={22} /></div>
                    <div className="dd-metric-value" style={{ color: '#6366f1' }}>{total ?? '—'}</div>
                    <div className="dd-metric-label">Total capturas</div>
                    <div className="dd-metric-sub" style={{ color: failed > 0 ? '#ef4444' : '#94a3b8' }}>{failed != null ? `${failed} fallidas` : '—'}</div>
                  </div>
                  <div className="dd-metric-card">
                    <div className="dd-metric-icon" style={{ color: pending > 0 ? '#f59e0b' : '#10b981' }}><HardDrive size={22} /></div>
                    <div className="dd-metric-value" style={{ color: pending > 0 ? '#f59e0b' : '#10b981' }}>{pending ?? 0}</div>
                    <div className="dd-metric-label">Pendientes upload</div>
                    <div className="dd-metric-sub" style={{ color: pending > 0 ? '#f59e0b' : '#10b981' }}>{pending > 0 ? '⚠ En cola' : '✓ Al día'}</div>
                  </div>
                </div>
              </div>
            )
          })()}

          {/* Hydro Sensor Status + pH Calibration (GP-HIDRO, legacy card) */}
          {deviceStatus?.sensor?.type === 'hydro' && (
            <div className="dd-info-card">
              <h3><FlaskConical size={14} style={{ marginRight: 6 }} />Calibración pH</h3>
              <div className="dd-info-rows">
                <div className="dd-info-row">
                  <span>Estado</span>
                  <strong style={{ color: deviceStatus.sensor.calibrated ? '#10b981' : '#f59e0b' }}>
                    {deviceStatus.sensor.calibrated ? '✓ Calibrado' : '⚠ Sin calibrar (defaults)'}
                  </strong>
                </div>
                {deviceStatus.sensor.trimV != null && (
                  <div className="dd-info-row"><span>TrimV actual</span><strong>{deviceStatus.sensor.trimV.toFixed(4)} V</strong></div>
                )}
                {deviceStatus.sensor.calibrated && (
                  <>
                    <div className="dd-info-row"><span>V @ pH 4.0</span><strong>{deviceStatus.sensor.calV4?.toFixed(6)} V</strong></div>
                    <div className="dd-info-row"><span>V @ pH 7.0</span><strong>{deviceStatus.sensor.calV7?.toFixed(6)} V</strong></div>
                    <div className="dd-info-row"><span>V @ pH 9.18</span><strong>{deviceStatus.sensor.calV10?.toFixed(6)} V</strong></div>
                    <div className="dd-info-row"><span>Slope ácido</span><strong>{deviceStatus.sensor.slopeAcid?.toFixed(4)}</strong></div>
                    <div className="dd-info-row"><span>Offset ácido</span><strong>{deviceStatus.sensor.offsetAcid?.toFixed(4)}</strong></div>
                    <div className="dd-info-row"><span>Slope alcalino</span><strong>{deviceStatus.sensor.slopeAlk?.toFixed(4)}</strong></div>
                    <div className="dd-info-row"><span>Offset alcalino</span><strong>{deviceStatus.sensor.offsetAlk?.toFixed(4)}</strong></div>
                  </>
                )}
                <div className="dd-info-row"><span>pH actual</span><strong style={{ color: '#22c55e' }}>{deviceStatus.sensor.ph?.toFixed(2)}</strong></div>
                <div className="dd-info-row"><span>TDS actual</span><strong style={{ color: '#f59e0b' }}>{Math.round(deviceStatus.sensor.tds)} ppm</strong></div>
                <div className="dd-info-row"><span>Temp. Agua</span><strong style={{ color: '#06b6d4' }}>{deviceStatus.sensor.waterTemp?.toFixed(1)}°C</strong></div>
              </div>
            </div>
          )}

          {/* Reset Diagnostics */}
          {(() => {
            const rr = device?.liveData?.lastResetReason ?? device?.lastResetReason
            const dr = device?.liveData?.lastDisconnectReason ?? device?.lastDisconnectReason
            const pnc = device?.liveData?.panicCount ?? device?.panicCount ?? null
            const wrc = device?.liveData?.wdtCount ?? device?.wdtCount ?? null
            const hasCrashCounters = (pnc != null && pnc > 0) || (wrc != null && wrc >= 3)
            if (rr == null && !dr && !hasCrashCounters) return null
            return (
              <div className="dd-section">
                <h2 className="dd-section-title"><AlertTriangle size={16} /> Diagnóstico de Reinicios</h2>
                {hasCrashCounters && (
                  <div style={{
                    background: 'rgba(239,68,68,0.12)',
                    border: '1px solid rgba(239,68,68,0.4)',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '0.85rem',
                    color: '#ef4444',
                  }}>
                    <AlertTriangle size={15} style={{ flexShrink: 0 }} />
                    <span>
                      <strong>Recovery mode activo</strong> —{pnc > 0 ? ` ${pnc} panic${pnc > 1 ? 's' : ''}` : ''}{wrc >= 3 ? `${pnc > 0 ? ',' : ''} ${wrc} WDT resets` : ''}.
                      El firmware redujo funciones para recuperarse.
                    </span>
                  </div>
                )}
                <div className="dd-info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
                  {rr != null && (
                    <div className="dd-info-card">
                      <h3>Último Reset</h3>
                      <div className="dd-info-rows">
                        <div className="dd-info-row">
                          <span>Razón</span>
                          <strong style={{ color: getResetReasonColor(rr) }}>
                            {getResetReasonLabel(rr)}
                          </strong>
                        </div>
                        <div className="dd-info-row">
                          <span>Código</span>
                          <strong style={{ color: getResetReasonColor(rr) }}>{rr}</strong>
                        </div>
                      </div>
                    </div>
                  )}
                  {dr && (
                    <div className="dd-info-card">
                      <h3>Última Desconexión</h3>
                      <div className="dd-info-rows">
                        <div className="dd-info-row">
                          <span>Razón</span>
                          <strong style={{ color: '#f59e0b' }}>{dr}</strong>
                        </div>
                      </div>
                    </div>
                  )}
                  {hasCrashCounters && (
                    <div className="dd-info-card">
                      <h3>Contadores de Crash</h3>
                      <div className="dd-info-rows">
                        {pnc != null && (
                          <div className="dd-info-row">
                            <span>Panic count</span>
                            <strong style={{ color: pnc > 0 ? '#ef4444' : '#10b981' }}>{pnc}</strong>
                          </div>
                        )}
                        {wrc != null && (
                          <div className="dd-info-row">
                            <span>WDT resets</span>
                            <strong style={{ color: wrc >= 3 ? '#f59e0b' : '#10b981' }}>{wrc}</strong>
                          </div>
                        )}
                        {(() => {
                          const hp = device?.liveData?.hpAtConnect ?? device?.hpAtConnect ?? device?.liveData?.heartbeat?.heap
                          if (hp == null) return null
                          const hpKB = Math.round(hp / 1024)
                          const color = hpKB < 10 ? '#ef4444' : hpKB < 30 ? '#f59e0b' : '#10b981'
                          return (
                            <div className="dd-info-row">
                              <span title="Heap al momento del último connect (antes de config recovery)">Heap al boot</span>
                              <strong style={{ color }}>{hpKB} KB{hpKB < 20 && <span style={{ fontSize: '11px', marginLeft: 4 }}>⚠️ BAJO</span>}</strong>
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}

          {/* Power Management Stats */}
          {(() => {
            const ld = device?.liveData
            const bo = ld?.brownoutCount ?? ld?.heartbeat?.brownoutCount
            const txp = ld?.txPower ?? ld?.heartbeat?.txPower
            const cpuF = ld?.cpuFreq ?? ld?.heartbeat?.cpuFreq
            if (bo == null && txp == null && cpuF == null) return null
            const getBrownoutColor = (count) => {
              if (count === 0) return '#10b981'
              if (count <= 2) return '#f59e0b'
              return '#ef4444'
            }
            const getTxPowerLabel = (dbm) => {
              if (dbm >= 17) return { label: 'Normal', color: '#10b981' }
              if (dbm >= 13) return { label: 'Reducido', color: '#f59e0b' }
              return { label: 'Mínimo', color: '#ef4444' }
            }
            const txInfo = txp != null ? getTxPowerLabel(txp) : null
            return (
              <div className="dd-section">
                <h2 className="dd-section-title"><Battery size={16} /> Gestión de Energía</h2>
                <div className="dd-info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                  {bo != null && (
                    <div className="dd-info-card">
                      <h3><Zap size={14} style={{ marginRight: 6 }} />Brownouts (24h)</h3>
                      <div className="dd-info-rows">
                        <div className="dd-info-row">
                          <span>Conteo</span>
                          <strong style={{ color: getBrownoutColor(bo), fontSize: '1.1rem' }}>{bo}</strong>
                        </div>
                        <div className="dd-info-row">
                          <span>Estado</span>
                          <strong style={{ color: getBrownoutColor(bo) }}>
                            {bo === 0 ? '✓ Sin problemas' : bo <= 2 ? '⚠ Caídas detectadas' : '🚨 Inestabilidad eléctrica'}
                          </strong>
                        </div>
                      </div>
                    </div>
                  )}
                  {txp != null && (
                    <div className="dd-info-card">
                      <h3><Radio size={14} style={{ marginRight: 6 }} />TX Power WiFi</h3>
                      <div className="dd-info-rows">
                        <div className="dd-info-row">
                          <span>Potencia</span>
                          <strong style={{ color: txInfo.color }}>{txp} dBm</strong>
                        </div>
                        <div className="dd-info-row">
                          <span>Modo</span>
                          <strong style={{ color: txInfo.color }}>{txInfo.label}</strong>
                        </div>
                      </div>
                    </div>
                  )}
                  {cpuF != null && (
                    <div className="dd-info-card">
                      <h3><Cpu size={14} style={{ marginRight: 6 }} />CPU</h3>
                      <div className="dd-info-rows">
                        <div className="dd-info-row">
                          <span>Frecuencia</span>
                          <strong style={{ color: cpuF >= 160 ? '#10b981' : cpuF >= 80 ? '#f59e0b' : '#ef4444' }}>
                            {cpuF} MHz
                          </strong>
                        </div>
                        <div className="dd-info-row">
                          <span>Modo</span>
                          <strong style={{ color: cpuF >= 160 ? '#10b981' : '#f59e0b' }}>
                            {cpuF >= 160 ? 'Normal' : 'Ahorro'}
                          </strong>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}

          {/* Switches */}
          {deviceStatus?.switches?.length > 0 && (
            <div className="dd-section">
              <h2 className="dd-section-title"><Power size={16} /> Switches ({deviceStatus.switches.length})</h2>
              <div className="dd-switches-grid">
                {deviceStatus.switches.map((sw, i) => (
                  <div key={i} className={`dd-switch-card ${sw.estado ? 'is-on' : ''}`}>
                    <div className="dd-switch-header">
                      <div className="dd-switch-name">
                        {renamingSwitch === i ? (
                          <div className="dd-rename-row">
                            <input
                              className="dd-rename-input"
                              value={renameValue}
                              onChange={e => setRenameValue(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && sendRename()}
                              autoFocus
                            />
                            <button className="dd-rename-btn ok" onClick={sendRename} disabled={renameLoading}><Check size={14} /></button>
                            <button className="dd-rename-btn cancel" onClick={() => setRenamingSwitch(null)}><X size={14} /></button>
                          </div>
                        ) : (
                          <>
                            <Power size={14} />
                            <span>{sw.nombre}</span>
                            <button className="dd-edit-name" onClick={() => startRename(i)} title="Renombrar"><Edit3 size={12} /></button>
                          </>
                        )}
                      </div>
                      <button
                        className={`dd-switch-toggle ${sw.estado ? 'on' : 'off'} ${switchTogglingStates[i] ? 'toggling' : ''}`}
                        onClick={() => toggleSwitch(i)}
                        disabled={switchTogglingStates[i]}
                      >
                        {switchTogglingStates[i] ? '⟳' : sw.estado ? 'ON' : 'OFF'}
                      </button>
                    </div>
                    <div className="dd-switch-meta">
                      <span className="dd-switch-tipo">{sw.config?.tipoFuncionamiento ?? '—'}</span>
                      {sw.config?.franjas?.length > 0 && sw.config.franjas.map((f, fi) => (
                        <span key={fi} className="dd-switch-franja">
                          {f.horaInicio}–{f.horaFin} · {f.config?.tipo || '—'}
                          {f.config?.horaPrendido && ` (ON ${f.config.horaPrendido}–${f.config.horaApagado})`}
                          {f.config?.tipo === 'intervalo' && (() => {
                            const on = (f.config.horasPrendido || 0) * 3600 + (f.config.minutosPrendido || 0) * 60 + (f.config.segundosPrendido || 0)
                            const off = (f.config.horasApagado || 0) * 3600 + (f.config.minutosApagado || 0) * 60 + (f.config.segundosApagado || 0)
                            const fmt = s => {
                              if (s >= 3600) return `${Math.floor(s / 3600)}h${s % 3600 >= 60 ? ` ${Math.floor((s % 3600) / 60)}m` : ''}`
                              if (s >= 60) return `${Math.floor(s / 60)}m${s % 60 ? ` ${s % 60}s` : ''}`
                              return `${s}s`
                            }
                            return ` (ON ${fmt(on)} / OFF ${fmt(off)})`
                          })()}
                        </span>
                      ))}
                      {sw.config?.dependency?.enabled && (
                        <span className="dd-switch-dep">Dep: {deviceStatus.switches[sw.config.dependency.masterSwitch]?.nombre || `Switch ${sw.config.dependency.masterSwitch + 1}`} ({sw.config.dependency.relation})</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Switch Timeline (24h) */}
          {deviceStatus?.switches?.length > 0 && (
            <div className="dd-section">
              <h2 className="dd-section-title"><Activity size={16} /> Timeline (24h)</h2>
              <div className="dd-timeline-wrap">
                <div className="dd-timeline-list">
                  {deviceStatus.switches.map((sw, si) => {
                    const franjas = sw.config?.franjas || []
                    return (
                      <div key={si} className="dd-timeline-row">
                        <div className="dd-timeline-label">
                          <span>{sw.nombre}</span>
                          <span className={`dd-timeline-badge ${sw.estado ? 'on' : 'off'}`}>{sw.estado ? 'ON' : 'OFF'}</span>
                        </div>
                        <div className="dd-timeline-bar">
                          {franjas.map((f, fi) => {
                            const startPct = timeToPercent(f.horaInicio)
                            const endPct = timeToPercent(f.horaFin)
                            const width = endPct > startPct ? endPct - startPct : (100 - startPct) + endPct
                            const onStart = f.config?.horaPrendido ? timeToPercent(f.config.horaPrendido) : null
                            const onEnd = f.config?.horaApagado ? timeToPercent(f.config.horaApagado) : null
                            const colors = ['rgba(99,102,241,0.4)', 'rgba(16,185,129,0.4)', 'rgba(245,158,11,0.4)', 'rgba(236,72,153,0.4)']
                            // Interval mode: generate repeating on/off blocks within the franja
                            const intervalBlocks = []
                            if (f.config?.tipo === 'intervalo') {
                              const onSec = (f.config.horasPrendido || 0) * 3600 + (f.config.minutosPrendido || 0) * 60 + (f.config.segundosPrendido || 0)
                              const offSec = (f.config.horasApagado || 0) * 3600 + (f.config.minutosApagado || 0) * 60 + (f.config.segundosApagado || 0)
                              const cycle = onSec + offSec
                              if (cycle > 0) {
                                const franjaStartSec = (parseInt(f.horaInicio) || 0) * 3600 + (parseInt(f.horaInicio?.split(':')[1]) || 0) * 60
                                const franjaEndSec = (parseInt(f.horaFin) || 0) * 3600 + (parseInt(f.horaFin?.split(':')[1]) || 0) * 60
                                const totalSec = franjaEndSec > franjaStartSec ? franjaEndSec - franjaStartSec : 86400 - franjaStartSec + franjaEndSec
                                const maxBlocks = Math.min(Math.ceil(totalSec / cycle), 200)
                                for (let b = 0; b < maxBlocks; b++) {
                                  const blockStart = b * cycle
                                  if (blockStart >= totalSec) break
                                  const blockEnd = Math.min(blockStart + onSec, totalSec)
                                  const bStartPct = ((franjaStartSec + blockStart) / 86400) * 100
                                  const bWidthPct = ((blockEnd - blockStart) / 86400) * 100
                                  intervalBlocks.push({ left: bStartPct, width: bWidthPct })
                                }
                              }
                            }
                            return (
                              <div key={fi}>
                                <div className="dd-timeline-franja" style={{ left: `${startPct}%`, width: `${width}%`, background: colors[fi % colors.length] }} />
                                {onStart != null && onEnd != null && (
                                  <div className="dd-timeline-on" style={{ left: `${onStart}%`, width: `${(onEnd > onStart ? onEnd - onStart : (100 - onStart) + onEnd)}%` }} />
                                )}
                                {intervalBlocks.map((blk, bi) => (
                                  <div key={`iv-${bi}`} className="dd-timeline-on" style={{ left: `${blk.left}%`, width: `${blk.width}%` }} />
                                ))}
                              </div>
                            )
                          })}
                          <div className="dd-timeline-now" style={{ left: `${nowPct}%` }} />
                          {[0, 6, 12, 18].map(h => (
                            <span key={h} className="dd-timeline-hour" style={{ left: `${(h / 24) * 100}%` }}>{h}h</span>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>              </div>            </div>
          )}
        </div>
      )}

      {/* ═══ TELEMETRY TAB ═══ */}
      {tab === 'telemetry' && (
        <div className="dd-content">
          <div className="dd-hours-bar">
            <span>Mostrar últimas:</span>
            {[1, 3, 6, 12, 24].map(h => (
              <button key={h} className={`dd-hour-btn ${telemetryHours === h ? 'active' : ''}`} onClick={() => setTelemetryHours(h)}>{h}h</button>
            ))}
          </div>
          <TelemetryChart mac={decodedMac} hours={telemetryHours} />
        </div>
      )}

      {/* ═══ COMMANDS TAB ═══ */}
      {tab === 'commands' && (
        <div className="dd-content dd-commands">
          <div className="dd-cmd-panel">
            {/* Quick Commands */}
            <div className="dd-cmd-section">
              <h3><Zap size={16} /> Comandos Rápidos</h3>
              <div className="dd-cmd-buttons">
                <button className="dd-cmd-btn status" onClick={() => sendCommand('STATUS')} disabled={cmdLoading}>
                  <Activity size={14} /> Solicitar Estado
                </button>
                <button className="dd-cmd-btn restart" onClick={() => sendCommand('RESTART')} disabled={cmdLoading}>
                  <RotateCcw size={14} /> Reiniciar
                </button>
              </div>
            </div>

            {/* Hardware Profile */}
            {device.model === 'GP-BOX' && (
              <>
                <div className="dd-cmd-divider" />
                <div className="dd-cmd-section">
                  <h3><HardDrive size={16} /> Perfil de Hardware</h3>
                  <div className="dd-profile-row">
                    <span className="dd-cmd-desc">Actual: <strong>{currentProfile || 'Desconocido'}</strong></span>
                    <div className="dd-profile-options">
                      {HARDWARE_PROFILES.map(p => (
                        <button
                          key={p.value}
                          className={`dd-profile-btn ${profileTarget === p.value ? 'selected' : ''} ${currentProfile === p.value ? 'current' : ''}`}
                          onClick={() => setProfileTarget(p.value === profileTarget ? '' : p.value)}
                          disabled={profileLoading}
                        >
                          {p.label}
                          {currentProfile === p.value && <span className="dd-profile-badge">actual</span>}
                        </button>
                      ))}
                    </div>
                    <button className="dd-cmd-btn restart" onClick={sendProfileChange} disabled={profileLoading || !profileTarget || profileTarget === currentProfile}>
                      <Send size={14} /> {profileLoading ? 'Enviando…' : 'Cambiar (OTA)'}
                    </button>
                  </div>
                </div>
              </>
            )}

            <div className="dd-cmd-divider" />

            {/* OTA Update */}
            <div className="dd-cmd-section">
              <h3><Download size={16} /> Actualización OTA</h3>
              <div className="dd-inline-row">
                <span className="dd-cmd-desc">Enviar la última versión de firmware disponible al dispositivo. Se reiniciará automáticamente.</span>
                <button className="dd-cmd-btn config" onClick={sendOTAUpdate} disabled={otaLoading}>
                  <Download size={14} /> {otaLoading ? 'Enviando…' : 'Actualizar Firmware'}
                </button>
              </div>
            </div>

            <div className="dd-cmd-divider" />

            {/* NVS Backup */}
            <div className="dd-cmd-section">
              <h3><Save size={16} /> Backup NVS</h3>
              <div className="dd-inline-row">
                <span className="dd-cmd-desc">Forzar guardado de la configuración actual en la memoria flash (NVS) del dispositivo.</span>
                <button className="dd-cmd-btn config" onClick={sendNvsBackup} disabled={nvsLoading}>
                  <Save size={14} /> {nvsLoading ? 'Enviando…' : 'Guardar Backup'}
                </button>
              </div>
            </div>

            <div className="dd-cmd-divider" />

            {/* pH Calibration — GP-HIDRO only */}
            {(device.model?.includes('HIDRO') || deviceStatus?.sensor?.type === 'hydro') && (
              <>
                <div className="dd-cmd-section">
                  <h3><FlaskConical size={16} /> Calibración pH</h3>
                  <p className="dd-cmd-desc">
                    Leer voltaje actual de la sonda e ingresar los voltajes de los 3 buffers para recalibrar.{' '}
                    {deviceStatus?.sensor?.calibrated
                      ? <span className="dd-ph-cal-badge ok">✓ Calibrado</span>
                      : <span className="dd-ph-cal-badge warn">⚠ Sin calibrar</span>
                    }
                  </p>
                  <p className="dd-cmd-desc" style={{ color: '#f59e0b', marginTop: '-0.25rem' }}>
                    ⚠️ Módulo GP-HIDRO: sonda <strong>invertida</strong> — pH bajo = voltaje bajo, pH alto = voltaje alto.
                  </p>

                  {deviceStatus?.sensor?.calibrated && (
                    <div className="dd-ph-cal-summary">
                      <span>V@pH4: <strong>{deviceStatus.sensor.calV4?.toFixed(6)} V</strong></span>
                      <span>V@pH7: <strong>{deviceStatus.sensor.calV7?.toFixed(6)} V</strong></span>
                      <span>V@pH9.18: <strong>{deviceStatus.sensor.calV10?.toFixed(6)} V</strong></span>
                      <span style={{ color: deviceStatus.sensor.calV4 < deviceStatus.sensor.calV7 ? '#22c55e' : '#f59e0b' }}>
                        {deviceStatus.sensor.calV4 < deviceStatus.sensor.calV7 ? '↑ invertida ✓' : '↓ estándar'}
                      </span>
                      <span>Slope ácido: <strong>{deviceStatus.sensor.slopeAcid?.toFixed(4)}</strong></span>
                      <span>Slope alcalino: <strong>{deviceStatus.sensor.slopeAlk?.toFixed(4)}</strong></span>
                    </div>
                  )}

                  <div className="dd-inline-row" style={{ marginBottom: '0.75rem' }}>
                    <span className="dd-cmd-desc">
                      Leer voltaje en tiempo real (estabilizar sonda antes de calibrar).
                      {phTrimV != null && <strong> Último TrimV: {phTrimV.toFixed(4)} V</strong>}
                    </span>
                    <button className="dd-cmd-btn status" onClick={readPhTrimV} disabled={phReadLoading}>
                      <Activity size={14} /> {phReadLoading ? 'Leyendo…' : 'Leer TrimV'}
                    </button>
                  </div>

                  <div className="dd-config-row">
                    <div className="dd-config-field">
                      <label>V @ pH 4.0 <span style={{ color: '#94a3b8', fontSize: '0.75em' }}>(más bajo)</span></label>
                      <input
                        type="number" step="0.0001" min="0" max="5"
                        placeholder="ej: 2.380"
                        value={phCalV4}
                        onChange={e => setPhCalV4(e.target.value)}
                        className="dd-config-input"
                      />
                    </div>
                    <div className="dd-config-field">
                      <label>
                        V @ pH{' '}
                        <select value={phRefNeutral} onChange={e => setPhRefNeutral(parseFloat(e.target.value))} style={{ background: 'transparent', border: 'none', color: 'inherit', fontSize: 'inherit', cursor: 'pointer' }}>
                          <option value={6.86}>6.86</option>
                          <option value={7.00}>7.00</option>
                        </select>
                      </label>
                      <input
                        type="number" step="0.0001" min="0" max="5"
                        placeholder="ej: 2.620"
                        value={phCalV7}
                        onChange={e => setPhCalV7(e.target.value)}
                        className="dd-config-input"
                      />
                    </div>
                    <div className="dd-config-field">
                      <label>V @ pH 9.18 <span style={{ color: '#94a3b8', fontSize: '0.75em' }}>(más alto)</span></label>
                      <input
                        type="number" step="0.0001" min="0" max="5"
                        placeholder="ej: 2.850"
                        value={phCalV10}
                        onChange={e => setPhCalV10(e.target.value)}
                        className="dd-config-input"
                      />
                    </div>
                    <button
                      className="dd-cmd-btn config"
                      onClick={sendPhCalibration}
                      disabled={phCalLoading || !phCalV4 || !phCalV7 || !phCalV10}
                    >
                      <Send size={14} /> {phCalLoading ? 'Enviando…' : 'Calibrar'}
                    </button>
                  </div>
                </div>

                <div className="dd-cmd-divider" />
              </>
            )}

            {/* Soil Calibration — GP-SOIL only */}
            {device.model?.includes('SOIL') && (() => {
              const soilSensors = deviceStatus?.device?.sensors || []
              const chCount = deviceStatus?.device?.channels ?? soilSensors.length
              const activeCh = soilSensors[soilCalChannel]
              return (
                <>
                  <div className="dd-cmd-section">
                    <h3><Droplets size={16} /> Calibración Humedad de Suelo</h3>
                    <p className="dd-cmd-desc">
                      Calibrá cada canal con la sonda en aire seco y sumergida en agua para máxima precisión.
                      El ADC es invertido: seco = valor alto (~4000), mojado = valor bajo (~500).{' '}
                      {chCount > 0
                        ? <span className="dd-ph-cal-badge ok">✓ {chCount} canal{chCount !== 1 ? 'es' : ''} detectado{chCount !== 1 ? 's' : ''}</span>
                        : <span className="dd-ph-cal-badge warn">⚠ Sin STATUS — presioná el botón STATUS primero</span>
                      }
                    </p>

                    {/* Selector de canal */}
                    <div className="dd-config-row" style={{ marginBottom: '0.75rem', alignItems: 'center' }}>
                      <div className="dd-config-field">
                        <label>Canal a calibrar</label>
                        <select
                          className="dd-config-input"
                          value={soilCalChannel}
                          onChange={e => { setSoilCalChannel(Number(e.target.value)); setSoilCalAck(null) }}
                        >
                          {chCount > 0
                            ? Array.from({ length: chCount }, (_, i) => (
                              <option key={i} value={i}>Canal {i}{soilSensors[i] ? ` (GPIO ${soilSensors[i].gpio}) — ${soilSensors[i].moisture?.toFixed(1) ?? '—'}%` : ''}</option>
                            ))
                            : <option value={0}>Canal 0</option>
                          }
                        </select>
                      </div>
                    </div>

                    {/* Valores actuales del canal seleccionado */}
                    {activeCh && (
                      <div className="dd-ph-cal-summary" style={{ marginBottom: '0.75rem' }}>
                        <span>Calibración actual — dryRaw: <strong>{activeCh.dryRaw ?? '—'}</strong></span>
                        <span>wetRaw: <strong>{activeCh.wetRaw ?? '—'}</strong></span>
                        <span>ADC raw ahora: <strong>{activeCh.raw ?? '—'}</strong></span>
                        <span>Humedad: <strong>{activeCh.moisture != null ? `${activeCh.moisture.toFixed(1)}%` : '—'}</strong></span>
                        <span>Estado: <strong>{activeCh.status ?? '—'}</strong></span>
                      </div>
                    )}

                    {/* ACK del último envío */}
                    {soilCalAck && (
                      <div className="dd-ph-cal-summary" style={{ marginBottom: '0.75rem', borderColor: soilCalAck.success !== false ? '#10b981' : '#ef4444' }}>
                        <span>✓ ACK ch{soilCalAck.channel} — dryRaw: <strong>{soilCalAck.dryRaw}</strong></span>
                        <span>wetRaw: <strong>{soilCalAck.wetRaw}</strong></span>
                        <span>Cambios aplicados: <strong>{soilCalAck.changed ? 'Sí' : 'No (mismo valor)'}</strong></span>
                      </div>
                    )}

                    {/* Inputs + botones */}
                    <div className="dd-config-row">
                      <div className="dd-config-field">
                        <label>dryRaw (en aire)</label>
                        <input
                          type="number" min="0" max="4095" step="1"
                          placeholder="ej: 4000"
                          value={soilCalDryRaw}
                          onChange={e => setSoilCalDryRaw(e.target.value)}
                          className="dd-config-input"
                        />
                      </div>
                      <div className="dd-config-field">
                        <label>wetRaw (en agua)</label>
                        <input
                          type="number" min="0" max="4095" step="1"
                          placeholder="ej: 500"
                          value={soilCalWetRaw}
                          onChange={e => setSoilCalWetRaw(e.target.value)}
                          className="dd-config-input"
                        />
                      </div>
                      <button
                        className="dd-cmd-btn config"
                        onClick={() => sendSoilCalibration(false)}
                        disabled={soilCalLoading || !soilCalDryRaw || !soilCalWetRaw}
                      >
                        <Send size={14} /> {soilCalLoading ? 'Enviando…' : 'Calibrar'}
                      </button>
                      <button
                        className="dd-cmd-btn status"
                        onClick={() => sendSoilCalibration(true)}
                        disabled={soilCalLoading}
                        title="Restablecer dryRaw=4000, wetRaw=500"
                      >
                        <RotateCcw size={14} /> Defaults
                      </button>
                    </div>
                  </div>

                  <div className="dd-cmd-divider" />
                </>
              )
            })()}

            {/* Reset Config */}
            <div className="dd-cmd-section">
              <h3><Trash2 size={16} /> Restablecer Configuración</h3>
              <div className="dd-inline-row">
                <span className="dd-cmd-desc">Reinicia todos los switches a modo básico, sin automatización ni dependencias.</span>
                <button className="dd-cmd-btn restart" onClick={sendResetConfig} disabled={resetLoading}>
                  <Trash2 size={14} /> {resetLoading ? 'Enviando…' : 'Restablecer'}
                </button>
              </div>
            </div>

            <div className="dd-cmd-divider" />

            {/* Raw MQTT */}
            <div className="dd-cmd-section">
              <h3><Terminal size={16} /> Comando MQTT Raw</h3>
              <p className="dd-cmd-desc">Enviar un payload JSON arbitrario al topic commands/down del dispositivo.</p>
              <div className="dd-raw-row">
                <textarea
                  className="dd-raw-input"
                  rows={3}
                  placeholder='{"type": "STATUS_REQUEST"}'
                  value={rawPayload}
                  onChange={e => setRawPayload(e.target.value)}
                />
                <button className="dd-cmd-btn config" onClick={sendRawMqtt} disabled={rawLoading || !rawPayload.trim()}>
                  <Send size={14} /> {rawLoading ? 'Enviando…' : 'Enviar'}
                </button>
              </div>
            </div>
          </div>

          {/* Event Log */}
          <div className="dd-log-section">
            <div className="dd-log-header">
              <h3>Log de eventos (tiempo real)</h3>
              <button className="dd-log-clear" onClick={() => setCmdLog([])}>Limpiar</button>
            </div>
            <div className="dd-log" ref={cmdLogRef}>
              {cmdLog.length === 0 ? (
                <div className="dd-log-empty">Los eventos aparecerán aquí…</div>
              ) : (
                cmdLog.map(entry => (
                  <div key={entry.id} className={`dd-log-entry ${entry.isError ? 'error' : ''}`}>
                    <span className="dd-log-ts">{entry.ts}</span>
                    <span className="dd-log-type">{entry.type}</span>
                    <span className="dd-log-data">{typeof entry.data === 'object' ? JSON.stringify(entry.data) : String(entry.data)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ LOGS TAB ═══ */}
      {tab === 'logs' && (
        <div className="dd-content">
          <DeviceLogs mac={decodedMac} />
          <DebugConsole mac={decodedMac} />
        </div>
      )}

      {/* ═══ HISTORY TAB ═══ */}
      {tab === 'history' && (
        <DeviceHistory mac={decodedMac} />
      )}

      {/* ═══ SECURITY TAB ═══ */}
      {tab === 'security' && (
        <DeviceCertificates mac={decodedMac} />
      )}

      {/* ═══ ERRORS TAB ═══ */}
      {tab === 'errors' && (
        <div className="dd-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertTriangle size={16} /> Historial de Errores
            </h3>
            <select
              value={errorsSeverity}
              onChange={e => setErrorsSeverity(e.target.value)}
              style={{ padding: '0.35rem 0.6rem', borderRadius: '0.4rem', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', color: '#e2e8f0', fontSize: '0.8rem', cursor: 'pointer' }}
            >
              <option value="">Todas las severidades</option>
              <option value="critical">Critical</option>
              <option value="error">Error</option>
              <option value="warn">Warning</option>
              <option value="info">Info</option>
            </select>
            <button
              onClick={() => loadDeviceErrors(errorsSeverity)}
              disabled={errorsLoading}
              style={{ padding: '0.35rem 0.75rem', borderRadius: '0.4rem', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', color: '#7dd3fc', fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            >
              <RefreshCw size={13} className={errorsLoading ? 'spin' : ''} /> Refrescar
            </button>
            {deviceErrors.length > 0 && (
              <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{deviceErrors.length} registros</span>
            )}
          </div>

          {errorsLoading ? (
            <div className="dd-empty">Cargando errores…</div>
          ) : deviceErrors.length === 0 ? (
            <div className="dd-empty">
              <CheckCircle size={32} style={{ opacity: 0.3 }} />
              <p>Sin errores registrados{errorsSeverity ? ` para severidad "${errorsSeverity}"` : ''}</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="dd-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>Hora</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>Severidad</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>Código</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>Componente</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>Mensaje</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>Cnt</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', color: '#94a3b8', fontWeight: 600 }}>Heap</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', color: '#94a3b8', fontWeight: 600 }}>Uptime</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>FW</th>
                  </tr>
                </thead>
                <tbody>
                  {deviceErrors.map((err, i) => {
                    const sevColor = err.severity === 'critical' ? '#ef4444' : err.severity === 'error' ? '#f97316' : err.severity === 'warn' ? '#f59e0b' : '#94a3b8'
                    const dt = new Date(err.timestamp)
                    const dateStr = dt.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })
                    const timeStr = dt.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                    return (
                      <tr key={`${err.mac}-${err.timestamp}-${i}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                        <td style={{ padding: '0.45rem 0.5rem', whiteSpace: 'nowrap', color: '#94a3b8' }}>
                          <span style={{ fontSize: '0.72rem' }}>{dateStr}</span><br />
                          <span>{timeStr}</span>
                        </td>
                        <td style={{ padding: '0.45rem 0.5rem' }}>
                          <span style={{ color: sevColor, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', padding: '0.15rem 0.4rem', borderRadius: '0.3rem', background: `${sevColor}1a` }}>
                            {err.severity}
                          </span>
                        </td>
                        <td style={{ padding: '0.45rem 0.5rem', fontFamily: 'monospace', color: '#e2e8f0', fontWeight: 600 }}>{err.code}</td>
                        <td style={{ padding: '0.45rem 0.5rem', color: '#94a3b8', fontFamily: 'monospace', fontSize: '0.78rem' }}>{err.component || '-'}</td>
                        <td style={{ padding: '0.45rem 0.5rem', color: '#cbd5e1', maxWidth: '280px' }}>
                          {err.message || '-'}
                          {err.replayedOffline && <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: '#64748b' }}>[offline]</span>}
                        </td>
                        <td style={{ padding: '0.45rem 0.5rem', textAlign: 'center', color: err.count > 1 ? '#f59e0b' : '#94a3b8' }}>{err.count || 1}</td>
                        <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', color: '#94a3b8', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          {err.freeHeap ? `${(err.freeHeap / 1024).toFixed(0)}k` : '-'}
                        </td>
                        <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.78rem' }}>
                          {err.uptimeSeconds ? (err.uptimeSeconds > 3600 ? `${Math.floor(err.uptimeSeconds / 3600)}h` : err.uptimeSeconds > 60 ? `${Math.floor(err.uptimeSeconds / 60)}m` : `${err.uptimeSeconds}s`) : '-'}
                        </td>
                        <td style={{ padding: '0.45rem 0.5rem', color: '#64748b', fontFamily: 'monospace', fontSize: '0.75rem' }}>{err.firmwareVersion || '-'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══ ASSIGN DEVICE MODAL ═══ */}
      {showAssignModal && (
        <div className="dd-modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="dd-modal" onClick={e => e.stopPropagation()}>
            <div className="dd-modal-header">
              <h3><UserPlus size={16} /> Asignar dispositivo a usuario</h3>
              <button className="dd-modal-close" onClick={() => setShowAssignModal(false)}><X size={16} /></button>
            </div>
            <div className="dd-modal-body">
              <div className="dd-modal-search">
                <Search size={14} />
                <input
                  type="text"
                  placeholder="Buscar por nombre o email…"
                  value={assignSearch}
                  onChange={e => setAssignSearch(e.target.value)}
                  autoFocus
                />
              </div>
              {assignClientsLoading ? (
                <div className="dd-empty" style={{ padding: '1.5rem' }}>Cargando usuarios…</div>
              ) : (
                <div className="dd-modal-list" style={{ maxHeight: assignSelectedUser ? '180px' : '320px' }}>
                  {assignClients
                    .filter(c => {
                      if (!assignSearch.trim()) return true
                      const q = assignSearch.toLowerCase()
                      return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q)
                    })
                    .map(c => {
                      const uid = c.userId || c.$id
                      const isSelected = assignSelectedUser && (assignSelectedUser.userId || assignSelectedUser.$id) === uid
                      return (
                        <div
                          key={uid}
                          className={`dd-modal-user-row ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleAssignUserSelect(c)}
                        >
                          <div className="dd-modal-user-avatar">
                            {(c.name || c.email || '?')[0].toUpperCase()}
                          </div>
                          <div className="dd-modal-user-info">
                            <strong>{c.name || '—'}</strong>
                            <span>{c.email || '—'}</span>
                          </div>
                          {isSelected && <Check size={16} style={{ color: '#10b981', flexShrink: 0 }} />}
                        </div>
                      )
                    })
                  }
                  {!assignClientsLoading && assignClients.filter(c => {
                    if (!assignSearch.trim()) return true
                    const q = assignSearch.toLowerCase()
                    return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q)
                  }).length === 0 && (
                      <div className="dd-empty" style={{ padding: '1rem' }}>Sin resultados</div>
                    )}
                </div>
              )}
              {assignSelectedUser && (
                <div style={{ marginTop: '0.75rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #888)', marginBottom: '0.4rem', fontWeight: 600 }}>
                    Sala (opcional)
                  </div>
                  {assignRoomsLoading ? (
                    <div className="dd-empty" style={{ padding: '0.5rem' }}>Cargando salas…</div>
                  ) : assignRooms.length === 0 ? (
                    <div className="dd-empty" style={{ padding: '0.5rem', fontSize: '0.8rem' }}>Sin salas configuradas</div>
                  ) : (
                    <div className="dd-modal-list" style={{ maxHeight: '140px' }}>
                      {assignRooms.map(room => {
                        const isRoomSelected = assignSelectedRoom?.$id === room.$id
                        return (
                          <div
                            key={room.$id}
                            className={`dd-modal-user-row ${isRoomSelected ? 'selected' : ''}`}
                            onClick={() => setAssignSelectedRoom(isRoomSelected ? null : room)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="dd-modal-user-info">
                              <strong>{room.name}</strong>
                              {room.cultivationType && <span>{room.cultivationType}</span>}
                            </div>
                            {isRoomSelected && <Check size={16} style={{ color: '#10b981', flexShrink: 0 }} />}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="dd-modal-footer">
              <button className="dd-cmd-btn" onClick={() => setShowAssignModal(false)}>Cancelar</button>
              <button
                className="dd-cmd-btn status"
                onClick={confirmAssign}
                disabled={!assignSelectedUser || assignLoading}
              >
                {assignLoading ? 'Asignando…' : `Asignar${assignSelectedUser ? ` a ${assignSelectedUser.name || assignSelectedUser.email}${assignSelectedRoom ? ` › ${assignSelectedRoom.name}` : ''}` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ UNASSIGN DEVICE MODAL ═══ */}
      {showUnassignModal && (
        <div className="dd-modal-overlay" onClick={() => setShowUnassignModal(false)}>
          <div className="dd-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="dd-modal-header">
              <h3><UserMinus size={16} /> Desasignar dispositivo</h3>
              <button className="dd-modal-close" onClick={() => setShowUnassignModal(false)}><X size={16} /></button>
            </div>
            <div className="dd-modal-body" style={{ padding: '1rem 1.25rem' }}>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.9rem', lineHeight: 1.5 }}>
                ¿Estás seguro? Esto eliminará el dispositivo <strong style={{ color: '#e2e8f0' }}>{decodedMac}</strong> de la cuenta de <strong style={{ color: '#e2e8f0' }}>{device?.authUser?.name || device?.authUser?.email || 'este usuario'}</strong> y lo removerá de todas sus salas.
              </p>
            </div>
            <div className="dd-modal-footer">
              <button className="dd-cmd-btn" onClick={() => setShowUnassignModal(false)}>Cancelar</button>
              <button
                className="dd-cmd-btn danger"
                onClick={confirmUnassign}
                disabled={unassignLoading}
              >
                {unassignLoading ? 'Desasignando…' : 'Confirmar desasignación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function DeviceCertificates({ mac }) {
  const [certs, setCerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(null)
  const [error, setError] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    adminApi.get(`/admin/devices/${encodeURIComponent(mac)}/certificates`)
      .then(data => setCerts(data.certificates || []))
      .catch(e => setError(e.message || 'Error al cargar certificados'))
      .finally(() => setLoading(false))
  }, [mac])

  useEffect(() => { load() }, [load])

  const handleDetach = async (certId) => {
    const pwd = window.prompt('Ingresá la contraseña de administrador para revocar el certificado:')
    if (pwd === null) return
    if (pwd !== 'GrowProARL') {
      alert('Contraseña incorrecta.')
      return
    }
    setDeleting(certId)
    try {
      await adminApi.delete(`/admin/devices/${encodeURIComponent(mac)}/certificates/${certId}`)
      load()
    } catch (e) {
      alert('Error al revocar certificado: ' + (e.message || 'Error desconocido'))
    } finally {
      setDeleting(null)
    }
  }

  if (loading) return <div className="dd-content"><div className="dd-empty">Cargando certificados...</div></div>
  if (error) return <div className="dd-content"><div className="dd-empty dd-empty--error">{error}</div></div>

  return (
    <div className="dd-content">
      <h2 className="dd-section-title"><Shield size={16} /> Certificados IoT</h2>
      {certs.length === 0 ? (
        <div className="dd-empty">No hay certificados asociados a este dispositivo</div>
      ) : (
        <div className="dd-cert-list">
          {certs.map(cert => (
            <div key={cert.certId} className="dd-cert-item">
              <div className="dd-cert-header">
                <span className={`dd-cert-badge dd-cert-badge--${cert.status?.toLowerCase()}`}>
                  {cert.status || 'UNKNOWN'}
                </span>
                <span className="dd-cert-id">{cert.certId}</span>
              </div>
              <div className="dd-cert-meta">
                <span className="dd-cert-date">
                  Creado: {cert.creationDate ? new Date(cert.creationDate).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                </span>
                {cert.lastModifiedDate && (
                  <span className="dd-cert-date">
                    Modificado: {new Date(cert.lastModifiedDate).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
              <button
                className="dd-cmd-btn restart"
                onClick={() => handleDetach(cert.certId)}
                disabled={deleting === cert.certId}
              >
                <Trash2 size={14} />
                {deleting === cert.certId ? 'Revocando...' : 'Desasociar & Revocar'}
              </button>
            </div>
          ))}
        </div>
      )}
      <button className="dd-cmd-btn config" onClick={load} style={{ marginTop: '12px' }}>
        <RotateCcw size={14} /> Actualizar
      </button>
    </div>
  )
}

function DeviceHistory({ mac }) {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    adminApi.get('/admin/audit-logs', { entity: 'device', entityId: mac })
      .then(data => setLogs(data.logs || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false))
  }, [mac])

  if (loading) return <div className="dd-content"><div className="dd-empty">Cargando historial...</div></div>

  return (
    <div className="dd-content">
      <h2 className="dd-section-title"><History size={16} /> Historial de Operaciones</h2>
      {logs.length === 0 ? (
        <div className="dd-empty">Sin operaciones registradas para este dispositivo</div>
      ) : (
        <div className="dd-history-list">
          {logs.map(log => (
            <div key={log.SK || log.$id} className="dd-history-item">
              <div className="dd-history-dot" />
              <div className="dd-history-content">
                <div className="dd-history-row">
                  <span className="dd-history-action">{log.action}</span>
                  <span className="dd-history-date">
                    {log.createdAt ? new Date(log.createdAt).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </span>
                </div>
                <div className="dd-history-desc">{log.description}</div>
                {log.userName && <span className="dd-history-user"><Activity size={12} /> {log.userName}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
